import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import React, { useEffect, useState, useContext, useRef, useLayoutEffect } from 'react';
import { doc, getDoc, onSnapshot, updateDoc, arrayUnion, serverTimestamp, writeBatch, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Link } from 'react-router-dom';
import { MembershipContext } from '../contexts/MembershipContext';
import { UserContext } from '../contexts/UserContext';
import { formatCurrencyInput } from '../utils/currencyUtils';
import { useContact } from '../contexts/ContactContext';
import { useNotifications } from '../contexts/NotificationContext';
import { getInitiatorPedalIds, MAX_INITIATOR_PEDALS } from '../utils/tradeUtils';
import { buildPedalSnapshotsPayload, resolvePedalDisplayFromTrade } from '../utils/tradeOfferPedalSnapshots';
import { placeholderFor, SKETCH_PLACEHOLDER_URL } from '../utils/listingPlaceholder';
import { beginOperation, markQueued, markConfirmed, markFailed, getPendingRecord, clearRecord, resolveAgainstOffer } from '../utils/offerResponseLedger';
import { trace } from '../utils/traceLog';
import { scrollPageToTop } from '../utils/scrollPageToTop';
import { useAdmin } from '../contexts/AdminContext';
import { Capacitor } from '@capacitor/core';
import imageCompression from 'browser-image-compression';
import AddressRequiredModal from '../components/AddressRequiredModal';
import PhotoGatePanel, { isPhotoGateClosed } from '../components/PhotoGatePanel';
import SellingDisabledBanner from '../components/SellingDisabledBanner';
import { useSellingDisabled } from '../hooks/useSellingDisabled';
import { validateMessage } from '../utils/profanityFilter';
import AutoGrowTextarea from '../components/AutoGrowTextarea';
import { formatRelativeTime } from '../utils/relativeTime';
import { parseOfferNote } from '../utils/offerNoteText';
import { safeCommentPhotoUrls } from '../utils/photoUrl';
import { useCommentCooldown } from '../hooks/useCommentCooldown';
import { cooldownMessage, isOfferComment } from '../utils/commentCooldown';
import { addTradeOffer } from '../components/AddTradeOffer';
import { maybePromptForPush } from '../services/softPushPrompt';
import { useReceivedOfferPushPrompt } from '../hooks/useReceivedOfferPushPrompt';
import { isOfferAwaitingMyResponse } from '../utils/offerAwaitingResponse';
import { getPedalsMatchingWantList } from '../utils/wantListMatches';
import { recordOfferIntentEvent, OFFER_INTENT_KINDS } from '../utils/offerIntentAnalytics';
import { isBulkOfferSendEnabled } from '../featureFlags';
import { reloadPage } from '../utils/reloadPage';
import AdminCancelledBanner from '../components/AdminCancelledBanner';
import AdminDiagnostics from '../components/AdminDiagnostics';
import { allowsTrade, offerModeOf } from '../utils/offerMode';
import { isAutoDeclined, autoDeclineReasonText } from '../utils/autoDeclineCopy';

const MAX_COMMENT_IMAGES = 3;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const COMPRESSION_THRESHOLD_MB = 1.5;

// "Send Offer to Others" modal — cap on how many duplicate offers can be sent
// in a single bulk action. Mirrors the per-offer flow exactly; each selection
// becomes its own independent tradeOffers doc with isBulkSend: true so the
// Cloud Function suppresses the per-offer sender confirmation/email.
const BULK_SEND_MAX = 10;
const BULK_SEND_VALUE_TOLERANCE = 30;
/** Extra buffer after exclusions — fetch stops once this many want-list-qualified matches after price filter are found. */
const BULK_SEND_CANDIDATE_FETCH_LIMIT = 50;
const BULK_SEND_FALLBACK_IMAGE = '/images/top-diagram.png';
const TERMINAL_TRADE_STATUSES = ['declined', 'expired', 'cancelled'];

// One-tap decline reasons. Declining has to be faster than ignoring, because an
// ignored offer expires silently at 48h and tells the sender nothing.
// "Cash added" is not a decline at all — it routes into the counter flow.
const DECLINE_REASONS = [
  { value: 'not_a_fit', label: 'Cool pedal, just not a fit for me' },
  { value: 'wants_only', label: "I'm only after items on my Wantlist" },
  { value: 'not_trading', label: "I'm not trading this one right now" },
  { value: 'needs_cash', label: "I'd consider it with cash added", routesToCounter: true }
];

// Real photo only. A photo-less listing resolves to null here so the render
// sites take their placeholder branch (which carries the onError fallback to
// the sketch and the "Photos coming" styling) instead of a bare <img>.
const getPedalImageUrl = (pedal) =>
  pedal?.images?.top || pedal?.images?.upper || pedal?.imageUrl || null;

const resolveHistoricalTradePedals = (data, liveInitiatorPedals, liveResponderPedal) => {
  const initiatorIds = getInitiatorPedalIds(data);
  const offeringPedals = initiatorIds
    .map((id) => resolvePedalDisplayFromTrade(data, id, liveInitiatorPedals.find((p) => p.id === id)))
    .filter(Boolean);
  const soughtPedal = data.responderPedalId
    ? resolvePedalDisplayFromTrade(data, data.responderPedalId, liveResponderPedal)
    : null;
  return { offeringPedals, soughtPedal };
};

const TheirPedalBadge = () => (
  <div style={{
    position: 'absolute',
    top: '-1px',
    right: '12px',
    backgroundColor: '#fef3c7',
    color: '#92400e',
    fontSize: '0.65rem',
    fontWeight: '700',
    padding: '2px 8px',
    borderRadius: '0 0 6px 6px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    border: '1px solid #C9A227',
    borderTop: 'none',
  }}>
    THEIR PEDAL
  </div>
);

const OfferPedalFields = ({ pedal, showListingLink = false, tradeStatus }) => {
  const imageUrl = getPedalImageUrl(pedal);
  // The photo is the tap target on a phone; the small "View listing" text
  // under the price was the only way in before (owner, 2026-11-01). Same
  // destination and same conditions as that link, so the photo is clickable
  // exactly when the text link shows. The photo ALONE: a first cut also
  // wrapped the make/model title, and because that wraps to a different
  // number of lines per pedal the clickable boundary ended at a different
  // place under every photo. The square photo gives one fixed boundary.
  const listingTo = showListingLink && pedal.id && !TERMINAL_TRADE_STATUSES.includes(tradeStatus)
    ? `/listing/${pedal.id}?fromOffer=true`
    : null;
  const photo = (
    <>
        {imageUrl ? (
          <img
            className="offer-pedal-img"
            src={imageUrl}
            alt={`${pedal.make} ${pedal.model}`}
            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px' }}
          />
        ) : (
          <img
            className="offer-pedal-img"
            src={placeholderFor(pedal).url}
            alt="Photos coming"
            onError={(e) => { if (!e.currentTarget.src.endsWith(SKETCH_PLACEHOLDER_URL)) e.currentTarget.src = SKETCH_PLACEHOLDER_URL; }}
            style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px', opacity: 0.85 }}
          />
        )}
    </>
  );
  return (
    <>
      <div style={{ marginBottom: '10px' }}>
        {listingTo ? (
          <Link to={listingTo} className="offer-pedal-tile-link" aria-label={`View listing: ${pedal.make} ${pedal.model}`}>
            {photo}
          </Link>
        ) : photo}
      </div>
      <div style={{ fontWeight: '700', fontSize: '1rem', marginBottom: '2px' }}>
        <div style={{ color: '#1f2937' }}>{pedal.make}</div>
        <div style={{ color: '#6b7280' }}>{pedal.model}</div>
      </div>
      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '4px' }}>
        <strong>Condition:</strong> {pedal.condition || '—'}
      </div>
      <div style={{ fontWeight: '700', fontSize: '1.25rem', color: '#1f2937', marginBottom: '6px' }}>
        ${pedal.price ?? 0}
      </div>
      {listingTo && (
        <Link to={listingTo} className="offer-listing-link">
          View listing →
        </Link>
      )}
    </>
  );
};

const getInitiatorPedalsTotalValue = (pedals) =>
  (pedals || []).reduce((sum, p) => sum + Number(p?.price || 0), 0);

/** Pedals + cash (offer/ask) — mirrors the cloned bulk-send offer terms. */
const getBulkSendComparableValue = (tradeData, initiatorPedals) => {
  const pedalTotal = getInitiatorPedalsTotalValue(initiatorPedals);
  const cash = Number(tradeData?.cashAmount || 0);
  if (cash <= 0) return pedalTotal;
  const type = tradeData?.type || tradeData?.cashType || 'straight';
  if (type === 'offer' || type === 'ask') return pedalTotal + cash;
  return pedalTotal;
};

const getBulkSendPriceWindow = (comparableValue) => ({
  minPrice: Math.max(0, comparableValue - BULK_SEND_VALUE_TOLERANCE),
  maxPrice: comparableValue + BULK_SEND_VALUE_TOLERANCE,
});

const getPrimaryPedalImage = (pedal) =>
  pedal?.images?.top || pedal?.images?.upper || (pedal?.placeholderImage?.url) || BULK_SEND_FALLBACK_IMAGE;



const OfferPage = () => {
  const { tradeId } = useParams();
  const { openContact } = useContact();
  const { notifications, markAsRead, loading: notificationsLoading } = useNotifications();
  const { isAdmin } = useAdmin();
  const { firestoreUser, loading: userDataLoading } = useContext(UserContext);
  const { disabled: sellingDisabled } = useSellingDisabled();

  const [showAddressModal, setShowAddressModal] = useState(false);

  // Utility to check if address is complete (needed for accepting trades)
  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };

  // Auto-open the inline address modal when the user lands here without a
  // complete address — same trigger as PurchaseOfferDetails/DirectOffer. This
  // page was the one offer surface that only showed the red "Add address"
  // text, which was easy to miss (found during Android testing, 2026-08-20).
  useEffect(() => {
    if (!userDataLoading && !isAddressComplete(firestoreUser?.shippingAddress)) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firestoreUser, userDataLoading]);

  const [tradeData, setTradeData] = useState(null);
  const [myPedal, setMyPedal] = useState(null);
  const [theirPedal, setTheirPedal] = useState(null);
  const [soughtPedal, setSoughtPedal] = useState(null);
  const [initiatorPedals, setInitiatorPedals] = useState([]);
  const [responseType, setResponseType] = useState('');
  const [counterAmount, setCounterAmount] = useState('0');
  const [counterOfferType, setCounterOfferType] = useState('ask'); // Default to 'ask'
  const [note, setNote] = useState('');
  const [declineReason, setDeclineReason] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  // Comment-box errors have their own slot, shown under the box. They used
  // to share errorMsg, which only renders inside the respond panel, so a
  // member waiting on the other party got no feedback at all (2026-10-23).
  const [commentError, setCommentError] = useState('');
  const [loading, setLoading] = useState(false);
  const [initialOfferMessage, setInitialOfferMessage] = useState('');
  const [myUserData, setMyUserData] = useState({ username: '', badge: '', photoURL: '', membershipTier: 'free', averageRating: 0 });
  const [theirUserData, setTheirUserData] = useState({ username: '', badge: '', photoURL: '', membershipTier: 'free', averageRating: 0 });
  const [currentUserId, setCurrentUserId] = useState(null);
  const [cancellingOffer, setCancellingOffer] = useState(false);
  // One Firestore write, no callable (Jason, 2026-10-19: the Trade Center's
  // cancel waited on the clearTradeFlags callable, i.e. a cold start of the
  // whole functions codebase, for a pending offer that has no inTrade flags to
  // clear — those are only set on acceptance). A 'cancelled' status keeps the
  // history, lets markPedalsInTrade clear any flags server-side, voids the
  // responder-badge turn, and is purged by cleanupDeclinedOffers after 7 days.
  const cancelOffer = async () => {
    if (!window.confirm('Cancel this offer? The other member will no longer be able to accept it.')) return;
    setCancellingOffer(true);
    try {
      await updateDoc(doc(db, 'tradeOffers', tradeId), {
        status: 'cancelled',
        cancelledBy: currentUserId,
        updatedAt: serverTimestamp(),
        offerUpdatedAt: serverTimestamp(),
        history: arrayUnion({
          type: 'response',
          from: currentUserId,
          responseType: 'cancel',
          // Either party may cancel (the last mover after a counter is the
          // recipient), so the line names nobody; the header shows who.
          message: 'Offer cancelled',
          timestamp: new Date(),
        }),
      });
      navigate('/trade-center');
    } catch (e) {
      console.error('❌ Cancel offer failed:', e);
      alert('Could not cancel this offer. Please try again.');
      setCancellingOffer(false);
    }
  };
  const [offerNotFound, setOfferNotFound] = useState(false);
  const [pedalUnavailable, setPedalUnavailable] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [sendingNote, setSendingNote] = useState(false);
  // Four comments per member per conversation in a rolling minute (owner
  // decision, 2026-11-01): counts the notes already in the stored history,
  // so a failed send never counts, and is re-checked inside handleSendNote.
  // The ref stops a same-tick double tap from sending twice.
  const commentCooldown = useCommentCooldown(tradeData?.history, currentUserId, isOfferComment);
  const sendNoteInFlightRef = useRef(false);
  const [pendingImageFiles, setPendingImageFiles] = useState([]);
  const [pendingPreviews, setPendingPreviews] = useState([]);
  const fileInputRef = useRef(null);
  const [notLoggedIn, setNotLoggedIn] = useState(false);
  const [counterPedalIds, setCounterPedalIds] = useState([]);
  const [initiatorListings, setInitiatorListings] = useState([]);
  const [loadingInitiatorListings, setLoadingInitiatorListings] = useState(false);
  const [showCounterModal, setShowCounterModal] = useState(false);
  const [counterConfirmed, setCounterConfirmed] = useState(false);
  const counterSnapshot = useRef(null);
  // Load-problem plumbing (2026-09-22, offer-comment notification stuck on
  // "Loading trade..." forever on iOS). Three silent dead ends existed: a
  // listener error only logged, an exception mid-callback died unhandled, and
  // a hung fetch produced no signal at all. loadProblem drives an honest
  // "trouble loading" + Retry state that can only ever appear BEFORE the first
  // successful render (pageResolvedRef); after that, transient errors keep the
  // old log-only behavior so a working page is never replaced. loadGenRef
  // invalidates in-flight fetches from an abandoned attempt or a superseded
  // snapshot so stale results can't overwrite newer state.
  const [loadProblem, setLoadProblem] = useState(null); // null | 'error' | 'timeout'
  // Nothing bumps this since Try again became a page reload (2026-10-12); kept
  // so the load effect's trace and deps stay as they were.
  const [loadAttempt] = useState(0);
  // Submission truthfulness (2026-09-25: a decline silently vanished when the
  // user navigated away before the write settled; the old UI implied success
  // long before anything was durable):
  //   'submitting' = before the critical write is issued (validation + preflight)
  //   'syncing'    = write issued locally, server acknowledgment pending
  //   'submitted'  = server acknowledged; the response is durable
  //   'failed'     = validation/preflight abort or write rejection
  const [submitPhase, setSubmitPhase] = useState('idle');
  // Verdict banner for a previous session's unconfirmed response, from the
  // local operation ledger (utils/offerResponseLedger.js).
  const [priorResponseNotice, setPriorResponseNotice] = useState(null);
  const loadGenRef = useRef(0);
  const pageResolvedRef = useRef(false);
  // Snapshot-storm defense (2026-09-25). Jason's device trace caught the
  // push-tap hang in the act: the offer listener delivered the SAME document
  // every ~8ms (125/sec, fromCache:false) — an SDK-level listen storm — and
  // the generation guard below turned that into a permanent hang: each
  // delivery invalidated the previous delivery's in-flight listing/profile
  // fetches 8ms in, so they could never complete and the page never resolved.
  // lastSnapVersionRef dedupes by document updateTime: identical re-deliveries
  // are skipped BEFORE the generation bump, so in-flight work survives and the
  // first delivery can finish even while the SDK storms underneath.
  const lastSnapVersionRef = useRef(null);
  const snapDupCountRef = useRef(0);
  const snapLogCountRef = useRef(0);
  const subCountRef = useRef(0);

  // "Send Offer to Others" modal state
  const [showBulkSendModal, setShowBulkSendModal] = useState(false);
  const [bulkSendCandidates, setBulkSendCandidates] = useState([]);
  const [bulkSendSelectedIds, setBulkSendSelectedIds] = useState([]);
  const [bulkSendNote, setBulkSendNote] = useState('');
  const [bulkSendSubmitting, setBulkSendSubmitting] = useState(false);
  const [bulkSendError, setBulkSendError] = useState('');
  const [bulkSendToast, setBulkSendToast] = useState('');
  // Per-tradeId guard so this effect runs at most once per offer page view.
  // Prevents the modal from re-opening on React re-renders within the same
  // offer view. Resets when the user navigates to a different /offer/:tradeId.
  const bulkSendCheckedTradeIdRef = useRef(null);

  const [isHeaderCompact, setIsHeaderCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (window.innerWidth <= 768) return false;
    return window.scrollY > 400;
  });
  const [showMobileChromeBar, setShowMobileChromeBar] = useState(false);
  const [mobileChromeBarHeight, setMobileChromeBarHeight] = useState(0);
  const mobileChromeInflowRef = useRef(null);
  const mobileChromeSlotRef = useRef(null);
  const mobileChromeBarHeightRef = useRef(0);
  const navbarHeightRef = useRef(125);
  const lastScrollYRef = useRef(typeof window !== 'undefined' ? window.scrollY : 0);
  const mobileChromeHandedOffRef = useRef(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const fromHistory = searchParams.get('from') === 'history';
  // Only the offer-submission flows pass justCreated:true via navigate state.
  // Page reloads, direct URL visits, links from trade-center / notifications /
  // emails all leave state empty, so the modal will not auto-open from them.
  const justCreatedFromSubmit = location.state?.justCreated === true;

  const offerBackPath = fromHistory ? '/offer-history' : '/trade-center';
  const offerBackLabel = fromHistory ? '← Back to Offer History' : '← Back to Offers';
  const offerChromeActionLabel = fromHistory ? 'Offer History' : 'All Offers';

  useLayoutEffect(() => {
    const readNavbarHeight = () => {
      const v = getComputedStyle(document.documentElement).getPropertyValue('--navbar-height').trim();
      navbarHeightRef.current = v ? parseFloat(v) : 125;
    };
    readNavbarHeight();
    window.addEventListener('resize', readNavbarHeight);
    return () => window.removeEventListener('resize', readNavbarHeight);
  }, []);

  // Always open an offer at the top. SPA navigation (including tapping a push
  // for a new/countered offer or a status change) preserves the previous scroll
  // position, which dropped users at the bottom "Respond" section — past the
  // pedal cards and the Trade History (where the latest counter's terms live).
  // Keyed on tradeId so each opened offer and any counter-counter re-nav resets.
  //
  // scrollPageToTop, not bare window.scrollTo: on iOS the WKWebView re-asserts
  // its own scroll offset shortly after a resume, so a single scrollTo at mount
  // silently loses (Jason observed push-tapped offers opening mid-page on
  // device, 2026-09-25 — the util's whole reason to exist).
  //
  // pageResolvedRef must reset with tradeId too: it silences the load
  // watchdog, the offer:load traces, and the resolve-time scroll "for this
  // offer" — without the reset, navigating from a resolved offer A to offer B
  // without unmounting left B with no watchdog, no traces, and no top-of-page
  // re-assert (2026-09-25 review finding).
  useEffect(() => {
    pageResolvedRef.current = false;
    return scrollPageToTop({ blur: false });
  }, [tradeId]);

  useEffect(() => {
    const handleScroll = () => {
      const y = window.scrollY;
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        const lastY = lastScrollYRef.current;
        const delta = y - lastY;
        const chromeSlotBottom = navbarHeightRef.current + mobileChromeBarHeightRef.current;
        const pastChromeSlot = y > chromeSlotBottom;
        const slotTop = mobileChromeSlotRef.current?.getBoundingClientRect().top ?? -1;

        if (y > chromeSlotBottom + 30) {
          mobileChromeHandedOffRef.current = false;
        }

        if (slotTop >= 0 || y <= 10) {
          setShowMobileChromeBar(false);
          if (slotTop >= 0) mobileChromeHandedOffRef.current = true;
        } else if (delta > 5) {
          setShowMobileChromeBar(false);
        } else if (delta < -5 && pastChromeSlot && !mobileChromeHandedOffRef.current) {
          setShowMobileChromeBar(true);
        }
        lastScrollYRef.current = y;
      } else {
        setIsHeaderCompact(y > 400);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    const el = mobileChromeInflowRef.current;
    if (!el) return;
    const measure = () => {
      const h = el.offsetHeight;
      if (h > 0) {
        mobileChromeBarHeightRef.current = h;
        setMobileChromeBarHeight(h);
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tradeData, offerNotFound, notLoggedIn, pedalUnavailable]);

  const offerHeaderLinkBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    borderRadius: '9999px',
    border: '1px solid #C89F3C',
    backgroundColor: 'white',
    color: '#374151',
    fontFamily: 'Nunito, sans-serif',
    fontWeight: 700,
    fontSize: '0.875rem',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  };

  const renderOfferMobileChromeBar = () => (
    <div className="op-compact-bar-inner">
      <span className="op-compact-title">Trade Offer</span>
      <span className="op-compact-spacer" aria-hidden="true" />
      <Link to={offerBackPath} className="op-link-btn" style={offerHeaderLinkBtnStyle}>
        {offerChromeActionLabel}
      </Link>
    </div>
  );

  const renderOfferStickyHeader = () => (
    <div className={`op-sticky-header ${isHeaderCompact ? 'is-compact' : ''}`}>
      <style>{`
        .op-compact-bar-inner {
          max-width: 1100px;
          margin: 0 auto;
          width: 100%;
          box-sizing: border-box;
          display: flex;
          justify-content: flex-start;
          align-items: center;
          gap: 12px;
        }
        .op-compact-title {
          font-family: Nunito, sans-serif;
          font-weight: 700;
          font-size: 1.125rem;
          color: #111827;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          min-width: 0;
          flex: 0 0 auto;
          text-align: left;
        }
        .op-compact-spacer {
          flex: 1 1 auto;
          min-width: 0;
        }
        .op-sticky-header .op-link-btn {
          flex-shrink: 0;
        }
        .op-sticky-header .op-header-expanded {
          display: block;
        }
        .op-sticky-header .op-mobile-chrome-slot {
          display: none;
        }
        .op-sticky-header .op-compact-bar-mobile-fixed {
          display: none;
        }
        @media (min-width: 769px) {
          .op-sticky-header .op-compact-bar-mobile-fixed {
            display: none !important;
          }
          .op-sticky-header .op-compact-bar {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 100;
            padding: 0.6rem 1rem 0.7rem 1rem;
            background-color: #F7F3EB;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            box-sizing: border-box;
            opacity: 0;
            pointer-events: none;
            transition: opacity 0.2s ease;
          }
          .op-sticky-header.is-compact .op-compact-bar {
            opacity: 1;
            pointer-events: auto;
          }
          .op-sticky-header .op-link-btn {
            padding: 0.6rem 1.4rem !important;
          }
        }
        @media (max-width: 768px) {
          .offer-page-content {
            padding-top: 0 !important;
          }
          .op-sticky-header .op-header-expanded {
            display: none !important;
          }
          .op-sticky-header .op-mobile-chrome-slot {
            display: block !important;
            position: relative;
            flex-shrink: 0;
            background-color: #F7F3EB;
            overflow: hidden;
            margin-left: -0.75rem;
            margin-right: -0.75rem;
            width: calc(100% + 1.5rem);
          }
          .op-sticky-header .op-compact-bar-inflow {
            display: block;
            padding: 0.6rem 1rem 1.5rem 1rem;
            background-color: #F7F3EB;
            box-sizing: border-box;
          }
          .op-sticky-header .op-compact-bar-inflow.is-suppressed {
            visibility: hidden;
            pointer-events: none;
          }
          .op-sticky-header .op-compact-bar-desktop {
            display: none !important;
          }
          .op-sticky-header .op-compact-bar-mobile-fixed {
            display: block;
          }
          .op-sticky-header .op-compact-bar {
            display: none;
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            z-index: 100;
            padding: 0.6rem 1rem 0.85rem 1rem;
            background-color: #F7F3EB;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
            box-sizing: border-box;
            pointer-events: none;
            transform: translateY(-100%);
            transition: transform 0.2s ease;
          }
          .op-sticky-header .op-compact-bar.is-mobile-visible {
            display: block;
            transform: translateY(0);
            pointer-events: auto;
          }
          .op-sticky-header .op-compact-title {
            font-size: calc(1rem + 1pt) !important;
            color: #5A9ECB !important;
          }
          .op-sticky-header .op-link-btn {
            padding: 0.5rem 1rem !important;
            font-size: 0.875rem !important;
          }
        }
        @media (min-width: 769px) {
          .offer-page {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .offer-page-tile-column {
            width: min(calc(50% + 2rem), 100%) !important;
            min-width: min(calc(800px + 2rem), 100%) !important;
            max-width: none !important;
            margin-left: auto !important;
            margin-right: auto !important;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            align-items: stretch;
          }
          .op-compact-bar-inner {
            width: min(calc(50% + 2rem), 100%) !important;
            min-width: min(calc(800px + 2rem), 100%) !important;
            max-width: none !important;
          }
          .offer-page-header-back-link {
            display: inline-flex !important;
            align-items: center;
          }
          .offer-page-tile-column .offer-page-main-container,
          .offer-page-tile-column .offer-page-lower-sections {
            width: 100% !important;
            min-width: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }
        @media (max-width: 768px) {
          .offer-page {
            padding: 0 0.75rem !important;
          }
        }
      `}</style>

      <div
        ref={mobileChromeSlotRef}
        className="op-mobile-chrome-slot"
        style={mobileChromeBarHeight > 0 ? { height: mobileChromeBarHeight, minHeight: mobileChromeBarHeight } : undefined}
      >
        <div
          ref={mobileChromeInflowRef}
          className={['op-compact-bar-inflow', showMobileChromeBar && 'is-suppressed'].filter(Boolean).join(' ')}
        >
          {renderOfferMobileChromeBar()}
        </div>
      </div>

      <div
        className={[
          'op-compact-bar',
          showMobileChromeBar && 'is-mobile-visible',
        ].filter(Boolean).join(' ')}
      >
        <div className="op-compact-bar-mobile-fixed">
          {renderOfferMobileChromeBar()}
        </div>
        <div className="op-compact-bar-desktop">
          {renderOfferMobileChromeBar()}
        </div>
      </div>
    </div>
  );

  const renderOfferPageHeaderBlock = () => (
    <div className="offer-page-header-block">
      <Link
        to={offerBackPath}
        className="offer-page-header-back-link"
        style={{
          color: '#5A9ECB',
          textDecoration: 'none',
          display: 'inline-flex',
          alignItems: 'center',
          marginBottom: '1rem',
          fontSize: '1.125rem',
        }}
      >
        {offerBackLabel}
      </Link>
      <h1
        className="font-bold text-gray-900 offer-page-header-title"
        style={{ fontSize: '2.25rem', fontWeight: '700', letterSpacing: '-0.025em', margin: 0 }}
      >
        Trade Offer
      </h1>
    </div>
  );

  const openCounterModal = () => {
    counterSnapshot.current = {
      pedalIds: [...counterPedalIds],
      amount: counterAmount,
      offerType: counterOfferType
    };
    setShowCounterModal(true);
  };

  const dismissCounterModal = () => {
    if (counterSnapshot.current) {
      setCounterPedalIds(counterSnapshot.current.pedalIds);
      setCounterAmount(counterSnapshot.current.amount);
      setCounterOfferType(counterSnapshot.current.offerType);
    }
    setResponseType('');
    setShowCounterModal(false);
  };

  const confirmCounterModal = () => {
    counterSnapshot.current = null;
    setCounterConfirmed(true);
    setShowCounterModal(false);
  };

  useEffect(() => {
    if (responseType === 'counter' && !counterConfirmed) {
      openCounterModal();
    }
  }, [responseType]);
  
  // Get membership context for fee calculations
  const { getTradeCompletionFee, getCashFeePercentage } = useContext(MembershipContext);


const getBadge = (count) => {
  if (count === undefined || count === null || isNaN(count)) return '0';
  return count.toString(); // Show exact number of completed trades, including 0
};

const getMembershipBadge = (membershipTier) => {
  switch (membershipTier) {
    case 'premium_basic':
      return (
        <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
          <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>9V</span>
          Member
        </div>
      );
    case 'premium_plus':
      return (
        <div className="flex items-center bg-purple-100 text-purple-800 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
          <span className="bg-purple-600 text-white px-1.5 py-0.5 rounded font-bold mr-1" style={{ fontSize: '11px' }}>18V</span>
          Member
        </div>
      );
    default:
      return (
        <div className="flex items-center bg-gray-100 text-gray-600 px-2 py-1 rounded-full font-medium" style={{ fontSize: '12px' }}>
          Free Member
        </div>
      );
  }
};

const getMembershipTierText = (tier) => {
  switch (tier) {
    case 'premium_basic': return '9V';
    case 'premium_plus': return '18V';
    default: return 'Free';
  }
};

const getMembershipProfileBadge = (membershipTier) => {
  switch (membershipTier) {
    case 'premium_basic':
      return (
        <div
          style={{
            position: 'absolute',
            top: '-4px',
            left: '-4px',
            backgroundColor: '#2563eb',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '4px',
            width: '20px',
            height: '12px',
            fontSize: '0.6rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #fff',
            boxShadow: '0 0 2px rgba(0,0,0,0.3)',
          }}
        >
          9V
        </div>
      );
    case 'premium_plus':
      return (
        <div
          style={{
            position: 'absolute',
            top: '-4px',
            left: '-4px',
            backgroundColor: '#7c3aed',
            color: 'white',
            fontWeight: 'bold',
            borderRadius: '4px',
            width: '20px',
            height: '12px',
            fontSize: '0.6rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid #fff',
            boxShadow: '0 0 2px rgba(0,0,0,0.3)',
          }}
        >
          18V
        </div>
      );
    default:
      return null;
  }
};

  // Cost breakdown calculation for displaying what user will pay/receive
  const calculateCostBreakdown = (cashAmount, offerType, pedalCount = 1) => {
    if (!currentUserId) return null;
    
    // Handle undefined/null cashAmount as 0
    const cashAmt = cashAmount || 0;
    
    const platformFee = getTradeCompletionFee(pedalCount);
    const cashFeePercentage = getCashFeePercentage() / 100;
    
    // Determine if current user pays or receives cash based on offer type
    let currentUserPaysCash = false;
    let currentUserReceivesCash = false;
    
    // Only set cash flow if cashAmount > 0
    if (cashAmt > 0) {
      if (offerType === 'ask') {
        // Current user is asking for cash - they receive
        currentUserPaysCash = false;
        currentUserReceivesCash = true;
      } else if (offerType === 'offer') {
        // Current user is offering cash - they pay
        currentUserPaysCash = true;
        currentUserReceivesCash = false;
      }
    }
    
    const cashFee = currentUserPaysCash ? Number((cashAmt * cashFeePercentage).toFixed(2)) : 0;
    const netCashReceived = currentUserReceivesCash ? Math.max(0, cashAmt - platformFee) : 0;
    const totalFees = currentUserReceivesCash
      ? netCashReceived
      : platformFee + (currentUserPaysCash ? cashAmt : 0) + cashFee;
    
    return {
      platformFee,
      pedalCount,
      cashFee,
      cashFeePercentage: getCashFeePercentage(),
      totalFees,
      currentUserPaysCash,
      currentUserReceivesCash,
      cashAmount: cashAmt,
      netCashReceived
    };
  };

  // Mark THIS trade's offer notifications as read whenever they appear. Scoped to
  // the offer currently on screen (n.tradeId === tradeId), so it only ever clears
  // notifications for the trade being viewed. Runs on every notifications update
  // (no one-shot guard) so it also catches notifications that load in late — e.g.
  // a cold start from a push tap, where the new_offer can arrive in a snapshot
  // after the first non-loading render — and it works when navigating between
  // /offer/:tradeId routes without a remount (the component instance persists).
  useEffect(() => {
    if (notificationsLoading) return;

    const relatedNotifications = notifications.filter(
      n => (n.type === 'new_offer' || n.type === 'offer_status_change' ||
            n.type === 'offer_comment' || n.type === 'offer_viewed') &&
           n.tradeId === tradeId &&
           !n.read
    );

    if (relatedNotifications.length > 0) {
      console.log(`📧 Marking ${relatedNotifications.length} trade offer notification(s) as read`);
      relatedNotifications.forEach(n => markAsRead(n.id));
    }
  }, [notifications, markAsRead, tradeId, notificationsLoading]);

  useEffect(() => {
  // LIVE subscription, not a one-shot getDoc — mirrors PurchaseOfferDetails.
  // A push tap targeting the route the user is already on is a router no-op
  // (no remount, no refetch), so trade pushes could land on stale offer
  // state; and a counter arriving mid-view now updates in place. Found
  // during Android testing, 2026-08-21.

  // Fresh load attempt: clear any prior problem banner and start this
  // attempt's watchdog. The timer is about first-render readiness, not any
  // single request — if the offer snapshot arrives but a listing/profile
  // fetch hangs, it still fires. It is cancelled on cleanup and permanently
  // silenced by pageResolvedRef once any resolved state has rendered.
  setLoadProblem(null);
  const loadStartedAt = Date.now();
  trace('offer:load:start', { attempt: loadAttempt });
  // Fresh attempt: reset the storm-defense counters (see refs above).
  lastSnapVersionRef.current = null;
  snapDupCountRef.current = 0;
  snapLogCountRef.current = 0;
  subCountRef.current = 0;
  const watchdog = setTimeout(() => {
    if (!pageResolvedRef.current) {
      trace('offer:load:timeout', { ms: Date.now() - loadStartedAt });
      setLoadProblem('timeout');
    }
  }, 12000);

  let unsubOffer = null;
  const unsubscribe = auth.onAuthStateChanged((user) => {
    if (!user) {
      setNotLoggedIn(true);
      return;
    }
    setNotLoggedIn(false);
    setCurrentUserId(user.uid);

    const offerRef = doc(db, 'tradeOffers', tradeId);
    if (unsubOffer) unsubOffer();
    // Subscription counter: if the storm turns out to be STACKED listeners
    // rather than one restarting stream, this makes it visible in the trace.
    subCountRef.current += 1;
    if (!pageResolvedRef.current) trace('offer:sub', { n: subCountRef.current });
    unsubOffer = onSnapshot(offerRef, async (offerSnap) => {
    // Storm dedupe: same server document version as the delivery already being
    // processed → skip entirely, BEFORE the generation bump, so it cannot
    // cancel in-flight work. hasPendingWrites deliveries are never skipped
    // (their updateTime still reflects the pre-write doc).
    const snapVersion = offerSnap.updateTime ? offerSnap.updateTime.toMillis() : null;
    if (
      snapVersion !== null &&
      lastSnapVersionRef.current === snapVersion &&
      !offerSnap.metadata.hasPendingWrites
    ) {
      snapDupCountRef.current += 1;
      if (snapDupCountRef.current <= 3 || snapDupCountRef.current % 200 === 0) {
        trace('offer:snap:dup', { n: snapDupCountRef.current });
      }
      return;
    }
    lastSnapVersionRef.current = snapVersion;
    // Each delivery invalidates older in-flight work. Unsubscribing stops
    // future listener events, but it does NOT cancel the awaited listing and
    // profile fetches an earlier callback already started — those promises
    // still resolve later, and the offer may have changed in between. Every
    // state update after an await is therefore guarded on this generation so
    // an abandoned attempt (Retry) or a superseded snapshot (counter arriving
    // mid-load) can never overwrite newer state with stale results.
    const gen = ++loadGenRef.current;
    // Trace snapshot deliveries only until first resolve, CAPPED: the
    // 2026-09-25 storm produced ~125 deliveries/sec and flooded the 200-event
    // ring buffer, evicting the notif:tap/wake/reconnect context we needed.
    // First 10 log individually, then every 100th.
    if (!pageResolvedRef.current) {
      snapLogCountRef.current += 1;
      if (snapLogCountRef.current <= 10 || snapLogCountRef.current % 100 === 0) {
        trace('offer:snap', {
          cache: !!offerSnap.metadata?.fromCache,
          exists: offerSnap.exists(),
          ms: Date.now() - loadStartedAt,
          n: snapLogCountRef.current,
        });
      }
    }
    try {
    if (!offerSnap.exists()) {
      // "Not found" must stay server-confirmed: a cached snapshot can report
      // a document as missing without the server having said so (e.g. while
      // the network is cycling during a Retry). A cached miss stays in the
      // loading/problem state instead of claiming the offer doesn't exist.
      if (!offerSnap.metadata.fromCache) setOfferNotFound(true);
      return;
    }

    const data = offerSnap.data();
    
    // 🔒 SECURITY: Verify user is part of this trade
    const isInitiator = user.uid === data.fromUserId;
    const isResponder = user.uid === data.toUserId;
    
    if (!isInitiator && !isResponder) {
      console.error('❌ User is not authorized to view this trade offer');
      setErrorMsg('You are not authorized to view this trade offer.');
      navigate('/');
      return;
    }
    
    setTradeData({ id: offerSnap.id, ...data });

    // Mark this offer as viewed by the current user, but only if:
    //   (a) the user is the active recipient of the current offer state, and
    //   (b) their existing viewedAt entry is missing or stale relative to offerUpdatedAt.
    // This write is idempotent — refreshes skip it. See firestore.rules
    // isViewedAtSelfOnly() for the matching server-side guard.
    // Recipient = whoever did NOT make the last offer/response action.
    // Status alone isn't sufficient because counter-counters keep the status
    // as 'countered' while flipping the direction.
    const actionEntries = (data.history || []).filter(e => e.type === 'offer' || e.type === 'response');
    const lastActionFrom = actionEntries.length > 0 ? actionEntries[actionEntries.length - 1].from : null;
    const isActiveStatus = data.status === 'pending' || data.status === 'countered';
    const currentRecipientUid = (isActiveStatus && lastActionFrom)
      ? (lastActionFrom === data.fromUserId ? data.toUserId : data.fromUserId)
      : null;
    if (currentRecipientUid === user.uid && data.offerUpdatedAt) {
      const stateMs = data.offerUpdatedAt.toMillis?.() ?? 0;
      const myViewedMs = data.viewedAt?.[user.uid]?.toMillis?.() ?? 0;
      if (stateMs > 0 && myViewedMs < stateMs) {
        // Fire-and-forget: don't block subsequent page loads on this write.
        updateDoc(offerRef, { [`viewedAt.${user.uid}`]: serverTimestamp() })
          .catch(err => console.warn('Failed to mark trade offer as viewed:', err));
      }
    }

    // 🔄 Set default counter offer type based on original trade type
    // If original trade was 'ask', responder should default to 'offer' (pay cash)
    // If original trade was 'offer', responder should default to 'ask' (receive cash)
    const defaultCounterType = data.type === 'ask' ? 'offer' : 'ask';
    setCounterOfferType(defaultCounterType);

    const initialMessage = data.history?.find((entry) => entry.type === 'offer')?.message || 'No offer message found.';
    setInitialOfferMessage(initialMessage);

    const allInitiatorPedalIds = getInitiatorPedalIds(data);
    const initiatorPedalSnaps = await Promise.all(
      allInitiatorPedalIds.map(id => getDoc(doc(db, 'listings', id)))
    );
    const responderPedalSnap = await getDoc(doc(db, 'listings', data.responderPedalId));
    if (gen !== loadGenRef.current) return; // superseded while fetching listings

    const isActiveOffer = ['pending', 'countered'].includes(data.status);

    let allInitiatorPedals = [];
    let responderPedalData = null;
    let hasUnavailablePedals = false;

    for (const snap of initiatorPedalSnaps) {
      if (snap.exists()) {
        allInitiatorPedals.push({ id: snap.id, ...snap.data() });
      } else {
        hasUnavailablePedals = true;
      }
    }

    if (responderPedalSnap.exists()) {
      responderPedalData = { id: responderPedalSnap.id, ...responderPedalSnap.data() };
    } else {
      hasUnavailablePedals = true;
    }

    if (allInitiatorPedals.some(p => p.active === false) || (responderPedalData && responderPedalData.active === false)) {
      hasUnavailablePedals = true;
    }

    // Active offers: block if any pedals are gone or deactivated
    if (hasUnavailablePedals && isActiveOffer) {
      setPedalUnavailable(true);
      return;
    }

    // Historical offers: prefer snapshots when listings are gone or deactivated
    if (!isActiveOffer) {
      const resolved = resolveHistoricalTradePedals(data, allInitiatorPedals, responderPedalData);
      allInitiatorPedals = resolved.offeringPedals;
      responderPedalData = resolved.soughtPedal;
    } else if (hasUnavailablePedals && data.pedalSnapshots) {
      const snapshots = data.pedalSnapshots;
      const snapshotInitiatorPedals = snapshots.initiatorPedals
        || (snapshots.initiatorPedal ? [snapshots.initiatorPedal] : []);

      if (allInitiatorPedals.length < allInitiatorPedalIds.length && snapshotInitiatorPedals.length > 0) {
        allInitiatorPedals = snapshotInitiatorPedals;
      }
      if (!responderPedalData && snapshots.responderPedal) {
        responderPedalData = snapshots.responderPedal;
      }
    }

    const initiatorPedalData = allInitiatorPedals[0] || null;

    setInitiatorPedals(allInitiatorPedals);
    setSoughtPedal(responderPedalData);

    const myPedalData = isInitiator ? initiatorPedalData : responderPedalData;
    const theirPedalData = isInitiator ? responderPedalData : initiatorPedalData;

    setMyPedal(myPedalData);
    setTheirPedal(theirPedalData);

    const myUserId = isInitiator ? data.fromUserId : data.toUserId;
    const theirUserId = isInitiator ? data.toUserId : data.fromUserId;
    // PII isolation: read sanitized public mirror.
    const myUserSnap = await getDoc(doc(db, 'publicProfiles', myUserId));
    const theirUserSnap = await getDoc(doc(db, 'publicProfiles', theirUserId));
    if (gen !== loadGenRef.current) return; // superseded while fetching profiles

    const myUser = myUserSnap.exists() ? myUserSnap.data() : {};
    const theirUser = theirUserSnap.exists() ? theirUserSnap.data() : {};

    const myCompleted = myUser.completedTrades || 0;
    const theirCompleted = theirUser.completedTrades || 0;

    setMyUserData({
      username: myUser.username || 'Unknown',
      badge: getBadge(myCompleted),
      photoURL: myUser.photoURL || '/images/default-avatar.png',
      membershipTier: myUser.membershipTier || 'free',
      averageRating: myUser.averageRating || 0
    });

    setTheirUserData({
      username: theirUser.username || 'Unknown',
      badge: getBadge(theirCompleted),
      photoURL: theirUser.photoURL || '/images/default-avatar.png',
      membershipTier: theirUser.membershipTier || 'free',
      averageRating: theirUser.averageRating || 0
    });
    } catch (err) {
      // Before the first successful render, surface the honest problem state;
      // after it, keep the previous behavior (log only, preserve the last
      // good content on screen) so a transient error never replaces a
      // working page.
      console.error('Trade offer load failed mid-callback:', err);
      if (gen === loadGenRef.current && !pageResolvedRef.current) {
        trace('offer:load:error', { where: 'callback', code: String(err?.code || err?.name || 'unknown') });
        setLoadProblem('error');
      }
    }
    }, (err) => {
      console.error('Trade offer snapshot error:', err);
      if (!pageResolvedRef.current) {
        trace('offer:load:error', { where: 'listener', code: String(err?.code || err?.name || 'unknown') });
        setLoadProblem('error');
      }
    });
  });

  // Captured so the cleanup mutates the ref object without reading
  // loadGenRef.current at cleanup time (satisfies react-hooks/exhaustive-deps;
  // this ref is a generation counter, not a DOM node, and bumping it in
  // cleanup is the whole point).
  const genRef = loadGenRef;
  return () => {
    clearTimeout(watchdog);
    // Invalidate this attempt's in-flight fetches so late results from an
    // abandoned attempt can't overwrite the next one's state.
    genRef.current++;
    if (unsubOffer) unsubOffer();
    unsubscribe();
  };
}, [tradeId, navigate, loadAttempt]);

  // First-render readiness. offerPageReady mirrors exactly what the render
  // gate below needs (terminal offers render from history/snapshots, active
  // offers need both pedals), and any resolved state — full page, not-found,
  // unavailable, logged-out — permanently silences the watchdog and problem
  // UI for this offer. After this flips, transient load errors preserve the
  // last good content instead of replacing a working page.
  // tradeData.id must match the CURRENT route's tradeId: on a counter-counter
  // re-nav the previous offer's data is still in state, and treating it as
  // "ready" would mark the new offer resolved before it loaded (silencing its
  // watchdog with stale content) — the counterpart of the pageResolvedRef
  // reset in the tradeId effect above.
  const offerPageReady =
    !!tradeData && tradeData.id === tradeId &&
    (TERMINAL_TRADE_STATUSES.includes(tradeData.status)
      ? (initiatorPedals.length > 0 || !!soughtPedal)
      : (!!myPedal && !!theirPedal));

  // Native "turn on notifications" pop-up when this offer is waiting on the
  // user (2026-10-07). Only once the page is ready; see the helper for the
  // exact rule (notes ignored, counters count, malformed history = no).
  useReceivedOfferPushPrompt(
    tradeId,
    offerPageReady && isOfferAwaitingMyResponse(tradeData, currentUserId, {
      routeId: tradeId, initiatorField: 'fromUserId', recipientField: 'toUserId',
    })
  );

  const offerPageResolved = offerPageReady || offerNotFound || pedalUnavailable || notLoggedIn;
  useEffect(() => {
    if (!offerPageResolved) return undefined;
    let cancelScroll;
    if (!pageResolvedRef.current) {
      trace('offer:load:resolved');
      // Re-assert top-of-page once real content commits. On a push-tap
      // resume the content resolves seconds after mount, outside the
      // mount-effect scroll's retry window, and iOS can restore the old
      // offset in between. One-time (guarded by pageResolvedRef), so it
      // can't fight a user scrolling after the page is up. The cleanup is
      // returned so a quick back-swipe cancels the pending re-asserts —
      // otherwise the orphaned timers yank the NEXT page to the top.
      cancelScroll = scrollPageToTop({ blur: false });
    }
    pageResolvedRef.current = true;
    setLoadProblem(null);
    return cancelScroll;
  }, [offerPageResolved]);

  // Try again reloads the page (see utils/reloadPage for why). The reply form
  // can't be on screen yet (the load-problem state is only reachable before
  // the offer first loads), but if a note was somehow typed, it asks first.
  const handleLoadRetry = () => {
    trace('offer:retry');
    reloadPage({ hasUnsavedWork: () => Boolean((note || '').trim() || (noteText || '').trim()) });
  };

  // Verdict check for a prior session's unconfirmed response. Runs on every
  // offer snapshot while no submit is in flight, so a late-landing write
  // upgrades itself to confirmed and quietly clears the banner. 'lost' is
  // asserted only when the offer provably hasn't changed since the attempt;
  // an independently changed offer gets the softer wording (see
  // offerResponseLedger.resolveAgainstOffer).
  useEffect(() => {
    if (!tradeData || !currentUserId || submitPhase !== 'idle') return;
    const record = getPendingRecord(tradeId);
    if (!record) {
      setPriorResponseNotice(null);
      return;
    }
    const verdict = resolveAgainstOffer(record, tradeData, currentUserId);
    if (verdict === 'confirmed') {
      markConfirmed(tradeId, record.opId);
      setPriorResponseNotice(null);
      return;
    }
    setPriorResponseNotice({ verdict, responseType: record.responseType });
  }, [tradeData, currentUserId, tradeId, submitPhase]);

  // Web-only exit warning, strictly BEFORE the critical write is issued:
  // pre-issuance, nothing exists anywhere, so leaving genuinely discards the
  // response. Once queued, the ledger makes any loss detectable on the next
  // visit, and beforeunload is only advisory anyway (never relied on to
  // finish a write; it does nothing for mobile/background termination).
  useEffect(() => {
    if (Capacitor.isNativePlatform() || submitPhase !== 'submitting') return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [submitPhase]);

  useEffect(() => {
    if (responseType !== 'counter' || !tradeData) return;
    setCounterPedalIds(getInitiatorPedalIds(tradeData));
    if (tradeData.cashAmount) {
      setCounterAmount(String(tradeData.cashAmount));
    }
    const fetchInitiatorListings = async () => {
      setLoadingInitiatorListings(true);
      try {
        const q = query(
          collection(db, 'listings'),
          where('ownerId', '==', tradeData.fromUserId),
          where('active', '==', true)
        );
        const snapshot = await getDocs(q);
        // Sale-only pedals cannot be asked for in a trade counter (plan §5).
        const listings = snapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter((l) => offerModeOf(l) !== 'sell');
        setInitiatorListings(listings);
      } catch (err) {
        console.error('Error fetching initiator listings:', err);
      } finally {
        setLoadingInitiatorListings(false);
      }
    };
    fetchInitiatorListings();
  }, [responseType, tradeData]);

  // ─── "Send Offer to Others" modal: auto-trigger after a fresh submission ───
  // Trigger contract: only fires when the user just submitted an offer via
  // DirectOffer/TradeOfferForm, which navigate here with state.justCreated=true.
  // All other navigation paths to /offer/:tradeId (notification clicks, trade
  // center / offer history links, direct URL visits, page reloads) leave
  // location.state empty, so the modal never opens for them.
  //
  // Conditions: page fully loaded, current user is the offer initiator, page
  // was reached via a fresh submission. We fetch want-list matches, filter out
  // the original recipient + the pedal already offered + any pedals the user
  // already has a pending offer on, then keep only pedals whose listing price
  // is within $30 of the offer's comparable value (initiator pedals + cash when
  // the repeated offer includes cash). Reuses tradeData + initiatorPedals already
  // on this page; want-list fetch uses a price-bounded Firestore query and skips
  // owner-profile enrichment so the modal stays lightweight.
  // Only opens the modal if at least one candidate remains. The ref ensures
  // the effect runs at most once per tradeId within a single page view.
  useEffect(() => {
    // Gated off 2026-11-01 (see isBulkOfferSendEnabled): the rest of this
    // effect and the modal below stay intact for a future redesign.
    if (!isBulkOfferSendEnabled()) return;
    if (!tradeData || !currentUserId) return;
    if (bulkSendCheckedTradeIdRef.current === tradeData.id) return;
    if (!justCreatedFromSubmit) return;
    if (currentUserId !== tradeData.fromUserId) return;
    if (!initiatorPedals.length || !theirPedal) return;

    bulkSendCheckedTradeIdRef.current = tradeData.id;

    const loadAndMaybeOpen = async () => {
      setBulkSendError('');
      try {
        // Pending offers the user has already sent — exclude their target pedals
        const pendingSnap = await getDocs(
          query(
            collection(db, 'tradeOffers'),
            where('fromUserId', '==', currentUserId),
            where('status', 'in', ['pending', 'countered'])
          )
        );
        const excludedPedalIds = new Set();
        pendingSnap.forEach((d) => {
          const rpid = d.data()?.responderPedalId;
          if (rpid) excludedPedalIds.add(rpid);
        });
        // Always exclude the pedal already offered in the current trade
        excludedPedalIds.add(tradeData.responderPedalId);

        const comparableValue = getBulkSendComparableValue(tradeData, initiatorPedals);
        const { minPrice, maxPrice } = getBulkSendPriceWindow(comparableValue);

        const matches = await getPedalsMatchingWantList(
          currentUserId,
          BULK_SEND_CANDIDATE_FETCH_LIMIT,
          { minPrice, maxPrice, skipOwnerEnrichment: true }
        );
        const candidates = matches.filter((p) =>
          p?.id &&
          p?.ownerId &&
          p.ownerId !== tradeData.toUserId &&
          !excludedPedalIds.has(p.id) &&
          allowsTrade(p) // sale-only listings take no trade offers (plan §5)
        );

        if (candidates.length === 0) {
          // No eligible matches — quietly skip. The ref already prevents
          // re-firing for this tradeId within the current page lifetime.
          return;
        }

        setBulkSendCandidates(candidates);
        setBulkSendSelectedIds([]);
        setBulkSendNote(tradeData.note || '');
        setShowBulkSendModal(true);
      } catch (err) {
        console.error('Failed to load bulk-send candidates:', err);
        // Don't show an error to the user — this is an opportunistic upsell,
        // not a blocking flow. Quietly skip.
      }
    };

    loadAndMaybeOpen();
  }, [tradeData, currentUserId, initiatorPedals, theirPedal, justCreatedFromSubmit]);

  const toggleBulkSendPedal = (pedalId) => {
    setBulkSendSelectedIds((prev) => {
      if (prev.includes(pedalId)) return prev.filter((id) => id !== pedalId);
      if (prev.length >= BULK_SEND_MAX) return prev;
      return [...prev, pedalId];
    });
  };

  const closeBulkSendModal = () => {
    if (bulkSendSubmitting) return;
    setShowBulkSendModal(false);
    setBulkSendCandidates([]);
    setBulkSendSelectedIds([]);
    setBulkSendNote('');
    setBulkSendError('');
  };

  const submitBulkSend = async () => {
    if (bulkSendSubmitting || bulkSendSelectedIds.length === 0) return;
    setBulkSendError('');

    // Validate the shared note client-side (profanity, external platforms, contact info)
    // so we don't fire N parallel offers that all reject with the same error inside
    // addTradeOffer's server-side validateMessage().
    const trimmedNote = (bulkSendNote || '').trim();
    if (trimmedNote) {
      const noteValidation = validateMessage(trimmedNote);
      if (!noteValidation.isValid) {
        setBulkSendError(noteValidation.error);
        return;
      }
    }

    setBulkSendSubmitting(true);

    // Snapshot the source offer terms — every cloned offer mirrors these.
    const sourcePedalIds = getInitiatorPedalIds(tradeData);
    const sourcePedalPrices = {};
    const sourcePedalDetails = {};
    for (const p of initiatorPedals) {
      sourcePedalPrices[p.id] = Number(p.price || 0);
      sourcePedalDetails[p.id] = {
        name: `${p.make || ''} ${p.model || ''}`.trim() || 'Pedal',
        imageUrl: getPrimaryPedalImage(p),
        condition: p.condition || null,
      };
    }

    const selectedPedalsRaw = bulkSendCandidates.filter((p) =>
      bulkSendSelectedIds.includes(p.id)
    );

    // Freshness check: re-fetch each selected listing right before submission
    // and skip any that have become inactive, entered a trade, been hidden, or
    // need reactivation in the time since the modal opened. This avoids creating
    // an offer for a pedal the owner can no longer accept on. Skipped pedals
    // count as failures for toast purposes (so the user sees N sent vs picked).
    const freshChecks = await Promise.all(
      selectedPedalsRaw.map(async (pedal) => {
        try {
          const snap = await getDoc(doc(db, 'listings', pedal.id));
          if (!snap.exists()) return { pedal, available: false };
          const data = snap.data();
          const available =
            data.active !== false &&
            data.inTrade !== true &&
            data.hiddenFromPedalboard !== true &&
            data.needsReactivation !== true &&
            allowsTrade(data);
          return { pedal, available };
        } catch (e) {
          // If we can't verify, skip rather than risk a bad offer
          console.warn(`Freshness check failed for ${pedal.id}:`, e);
          return { pedal, available: false };
        }
      })
    );

    const selectedPedals = freshChecks.filter((c) => c.available).map((c) => c.pedal);
    const skippedCount = selectedPedalsRaw.length - selectedPedals.length;

    if (selectedPedals.length === 0) {
      setBulkSendSubmitting(false);
      setBulkSendError(
        'None of the selected pedals are still available. Please refresh and try again.'
      );
      return;
    }

    const results = await Promise.allSettled(
      selectedPedals.map((targetPedal) => {
        const responderPedalDetail = {
          name: `${targetPedal.make || ''} ${targetPedal.model || ''}`.trim() || 'Pedal',
          imageUrl: getPrimaryPedalImage(targetPedal),
          condition: targetPedal.condition || null,
        };
        const pedalSnapshots = buildPedalSnapshotsPayload(initiatorPedals, targetPedal);
        return addTradeOffer({
          matchId: null,
          initiatorId: currentUserId,
          initiatorEmail: auth.currentUser?.email,
          responderId: targetPedal.ownerId,
          initiatorPedalIds: sourcePedalIds,
          responderPedalId: targetPedal.id,
          cashAdjustmentType: tradeData.type,
          cashAdjustmentAmount: tradeData.cashAmount || 0,
          note: bulkSendNote,
          initiatorPedalPrices: sourcePedalPrices,
          responderPedalPrice: Number(targetPedal.price || 0),
          initiatorPedalDetails: sourcePedalDetails,
          responderPedalDetail,
          pedalSnapshots,
          isBulkSend: true
        });
      })
    );

    // Map results to their source pedals BEFORE filtering — Promise.allSettled
    // preserves input order, so results[i] corresponds to selectedPedals[i].
    // We pair them first so subsequent filtering doesn't desync the indexes.
    const resultsWithPedals = results.map((r, idx) => ({
      result: r,
      pedal: selectedPedals[idx]
    }));
    const successful = resultsWithPedals.filter((rp) => rp.result.status === 'fulfilled');
    const submitFailed = resultsWithPedals.filter((rp) => rp.result.status === 'rejected');
    const failed = submitFailed.length + skippedCount; // unavailable pedals also count as failures
    const successfulTradeIds = successful.map((rp) => rp.result.value);

    // Per-offer analytics (mirrors DirectOffer flow)
    successful.forEach(({ result, pedal }) => {
      void recordOfferIntentEvent(OFFER_INTENT_KINDS.TRADE_OFFER_SUBMITTED, {
        listingId: pedal.id,
        tradeOfferId: result.value,
        source: 'bulk_send'
      });
    });

    if (successful.length > 0) {
      // Single bulk summary notification — replaces the N suppressed
      // individual 'offer_sent' confirmations.
      try {
        await addDoc(
          collection(db, 'users', currentUserId, 'notifications'),
          {
            type: 'offers_sent_bulk',
            title: `${successful.length} Trade Offer${successful.length === 1 ? '' : 's'} Sent!`,
            message: `Your trade offer was sent to ${successful.length} other user${successful.length === 1 ? '' : 's'}. They each have 48 hours to respond.`,
            tradeId: null,
            matchId: null,
            offerId: null,
            purchaseId: null,
            metadata: {
              tradeIds: successfulTradeIds,
              originalTradeId: tradeData.id,
              count: successful.length
            },
            read: false,
            createdAt: serverTimestamp(),
            readAt: null
          }
        );
      } catch (err) {
        console.error('Failed to write bulk summary notification:', err);
      }
    }

    setBulkSendSubmitting(false);

    if (failed === 0) {
      setBulkSendToast(`${successful.length} offer${successful.length === 1 ? '' : 's'} sent successfully.`);
    } else if (successful.length === 0) {
      setBulkSendError('All offers failed to send. Please try again.');
      return;
    } else {
      setBulkSendToast(`${successful.length} sent, ${failed} failed.`);
    }

    setShowBulkSendModal(false);
    setBulkSendCandidates([]);
    setBulkSendSelectedIds([]);
    setBulkSendNote('');

    // Contextual soft-ask for push permission (native-only, no-op on web).
    maybePromptForPush('make_offer');

    setTimeout(() => setBulkSendToast(''), 8000);
  };

  const toggleCounterPedal = (pedalId) => {
    setCounterPedalIds(prev => {
      if (prev.includes(pedalId)) return prev.filter(id => id !== pedalId);
      if (prev.length >= MAX_INITIATOR_PEDALS) return prev;
      return [...prev, pedalId];
    });
  };

  const handleCommentFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;

    const remaining = MAX_COMMENT_IMAGES - pendingImageFiles.length;
    if (remaining <= 0) {
      setCommentError(`Maximum ${MAX_COMMENT_IMAGES} images per comment.`);
      return;
    }

    for (const file of files.slice(0, remaining)) {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setCommentError('Please use JPEG, PNG, or WebP images only.');
        return;
      }
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setCommentError('Each image must be under 10MB.');
        return;
      }
    }

    const toAdd = files.slice(0, remaining);
    const newPreviews = toAdd.map((f) => URL.createObjectURL(f));
    setPendingImageFiles((prev) => [...prev, ...toAdd]);
    setPendingPreviews((prev) => [...prev, ...newPreviews]);
    setCommentError('');
  };

  const removeCommentImage = (index) => {
    setPendingImageFiles((prev) => prev.filter((_, i) => i !== index));
    setPendingPreviews((prev) => {
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleSendNote = async () => {
    if (sendNoteInFlightRef.current) return;
    const hasText = noteText.trim().length > 0;
    const hasImages = pendingImageFiles.length > 0;
    if (!hasText && !hasImages) return;
    const gate = commentCooldown.check();
    if (!gate.allowed) {
      // Draft text and pending photos are left untouched.
      setCommentError(cooldownMessage(gate.remainingMs));
      return;
    }

    if (hasText) {
      const validation = validateMessage(noteText);
      if (!validation.isValid) {
        setCommentError(validation.error);
        return;
      }
    }

    try {
      sendNoteInFlightRef.current = true;
      setSendingNote(true);
      const user = auth.currentUser;
      if (!user) throw new Error('User not authenticated');

      let imageUrls = [];
      if (pendingImageFiles.length > 0) {
        const storage = getStorage();
        const basePath = `offer_message_photos/${tradeId}`;
        const timestamp = Date.now();
        for (let i = 0; i < pendingImageFiles.length; i++) {
          const file = pendingImageFiles[i];
          let fileToUpload = file;
          const originalSizeMB = file.size / 1024 / 1024;
          if (originalSizeMB > COMPRESSION_THRESHOLD_MB) {
            try {
              const compressed = await imageCompression(file, {
                maxSizeMB: 1.5,
                maxWidthOrHeight: 1800,
                useWebWorker: false,
                quality: 0.85,
                fileType: 'image/jpeg',
              });
              fileToUpload = compressed;
            } catch (compressionErr) {
              console.warn('Comment image compression failed, using original:', compressionErr);
            }
          }
          const unique = `${user.uid}_${timestamp}_${i}_${Math.random().toString(36).slice(2, 9)}`;
          const storageRef = ref(storage, `${basePath}/${unique}.jpg`);
          await uploadBytes(storageRef, fileToUpload);
          const url = await getDownloadURL(storageRef);
          imageUrls.push(url);
        }
        pendingPreviews.forEach((url) => URL.revokeObjectURL(url));
        setPendingImageFiles([]);
        setPendingPreviews([]);
      }

      const historyEntry = {
        type: 'note',
        from: user.uid,
        message: noteText.trim(),
        timestamp: new Date()
      };
      if (imageUrls.length > 0) historyEntry.imageUrls = imageUrls;

      const tradeRef = doc(db, 'tradeOffers', tradeId);
      await updateDoc(tradeRef, {
        updatedAt: serverTimestamp(),
        history: arrayUnion(historyEntry)
      });

      setNoteText('');

      const offerSnap = await getDoc(tradeRef);
      if (offerSnap.exists()) {
        setTradeData({ id: offerSnap.id, ...offerSnap.data() });
      }
    } catch (err) {
      console.error('Error sending comment:', err);
      setCommentError('Failed to send comment. Please try again.');
    } finally {
      sendNoteInFlightRef.current = false;
      setSendingNote(false);
    }
  };

  const handleResponse = async () => {
    if (!responseType) {
      setErrorMsg('Please select a response option.');
      return;
    }

    // Block accepting a trade if address is incomplete (both parties need addresses to ship)
    if (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress)) {
      setShowAddressModal(true);
      return;
    }

    let ledgerOpId = null;
    try {
      setLoading(true);
      setErrorMsg('');
      setSubmitPhase('submitting');

      const user = auth.currentUser;
      console.log('🧪 Authenticated user:', user?.uid);
      if (!user) throw new Error('User not authenticated');

      // Local operation record: if this submit is lost before the write
      // settles (navigation, closed tab, dead connection), the next visit to
      // this offer shows an honest verdict instead of silence.
      ledgerOpId = beginOperation({
        offerId: tradeId,
        responseType,
        preflightUpdatedAt: tradeData.updatedAt?.seconds || tradeData.updatedAt || 0,
      });

      const trimmedNote = (note || '').trim();
      if (trimmedNote) {
        const validation = validateMessage(trimmedNote);
        if (!validation.isValid) {
          markFailed(tradeId, ledgerOpId, 'validation:note');
          setSubmitPhase('failed');
          setErrorMsg(validation.error);
          setLoading(false);
          return;
        }
      }

      // Selling-suspension guard. Trades transfer gear and so count as
      // selling for both the initiator and responder. Decline is always
      // allowed.
      if (sellingDisabled && (responseType === 'accept' || responseType === 'counter')) {
        markFailed(tradeId, ledgerOpId, 'selling-paused');
        setSubmitPhase('failed');
        setErrorMsg('Selling, trading & listing are currently paused on your account due to an outstanding recovery invoice. You can still decline this trade.');
        setLoading(false);
        return;
      }

      const tradeRef = doc(db, 'tradeOffers', tradeId);
      console.log('✅ responseType:', responseType);

      let messageText = '';
      if (responseType === 'accept') messageText = 'Trade accepted!';
      if (responseType === 'decline') {
        messageText = declineReason ? `Passed on this trade: ${declineReason}` : 'Passed on this trade.';
      }
      if (responseType === 'counter') {
        if (counterPedalIds.length === 0) {
          markFailed(tradeId, ledgerOpId, 'validation:counter');
          setSubmitPhase('failed');
          setErrorMsg('At least one pedal must be selected.');
          setLoading(false);
          return;
        }
        const rawAmount = Number(counterAmount || 0);
        if (isNaN(rawAmount) || rawAmount < 0) {
          markFailed(tradeId, ledgerOpId, 'validation:counter');
          setSubmitPhase('failed');
          setErrorMsg('Please enter a valid positive amount.');
          setLoading(false);
          return;
        }
        if (rawAmount > 2000) {
          markFailed(tradeId, ledgerOpId, 'validation:counter');
          setSubmitPhase('failed');
          setErrorMsg('Counter amount cannot exceed $2,000.');
          setLoading(false);
          return;
        }

        const validatedAmount = Math.abs(Math.round(rawAmount * 100) / 100);
        const dollarAmount = `$${validatedAmount.toFixed(2)}`;

        const counterAction = counterOfferType === 'ask' ? 'Asking for' : 'Offering';
        const originalPedalIds = getInitiatorPedalIds(tradeData);
        const pedalsChanged = counterPedalIds.length !== originalPedalIds.length ||
          counterPedalIds.some(id => !originalPedalIds.includes(id));
        const pedalNote = pedalsChanged ? ' Modified pedals.' : '';
        messageText = `Counter Offer:${pedalNote} ${counterAction} ${dollarAmount}.`;
      }

      // Determine the other party in the trade (not the current user)
      const otherUserId = tradeData.fromUserId === user.uid ? tradeData.toUserId : tradeData.fromUserId;
      
      // Pre-flight check: re-read the trade to ensure we're not acting on stale data
      const freshSnap = await getDoc(tradeRef);
      if (!freshSnap.exists()) {
        throw new Error('This trade offer no longer exists.');
      }
      const freshData = freshSnap.data();
      const localStatus = tradeData.status;
      const localUpdatedAt = tradeData.updatedAt?.seconds || tradeData.updatedAt || 0;
      const freshUpdatedAt = freshData.updatedAt?.seconds || freshData.updatedAt || 0;

      if (freshData.status !== localStatus || freshUpdatedAt !== localUpdatedAt) {
        markFailed(tradeId, ledgerOpId, 'stale-offer');
        setSubmitPhase('failed');
        setTradeData({ id: freshSnap.id, ...freshData });
        setErrorMsg('This offer has been updated by the other party. The page has been refreshed. Please review the latest terms.');
        setLoading(false);
        return;
      }

      // Create pedal snapshots BEFORE updating trade for history preservation
      let pedalSnapshots = null;
      if (responseType === 'accept' && tradeData.responderPedalId) {
        console.log('📸 Creating pedal snapshots for trade history preservation');
        try {
          const allPedalIds = getInitiatorPedalIds(tradeData);
          const initiatorSnaps = await Promise.all(
            allPedalIds.map(id => getDoc(doc(db, 'listings', id)))
          );
          const responderPedalSnap = await getDoc(doc(db, 'listings', tradeData.responderPedalId));
          
          const allInitiatorSnapshots = initiatorSnaps
            .filter(s => s.exists())
            .map(s => ({ id: s.id, ...s.data() }));

          if (allInitiatorSnapshots.length > 0 && responderPedalSnap.exists()) {
            pedalSnapshots = {
              initiatorPedal: allInitiatorSnapshots[0],
              initiatorPedals: allInitiatorSnapshots,
              responderPedal: { id: responderPedalSnap.id, ...responderPedalSnap.data() },
              snapshotCreatedAt: new Date().toISOString()
            };
            console.log('✅ Pedal snapshots created successfully');
          }
        } catch (snapshotError) {
          console.error('❌ Error creating pedal snapshots:', snapshotError);
        }
      }

      // THE CRITICAL WRITE. Everything the user's response depends on is in
      // this one update; nothing after it may hold the confirmation hostage.
      markQueued(tradeId, ledgerOpId, freshUpdatedAt);
      setSubmitPhase('syncing');
      await updateDoc(tradeRef, {
        status: responseType === 'counter' ? 'countered' : (responseType === 'accept' ? 'accepted' : (responseType === 'decline' ? 'declined' : responseType)),
        updatedAt: serverTimestamp(),
        offerUpdatedAt: serverTimestamp(),
        ...(responseType === 'counter' && {
          cashAmount: Number(counterAmount),
          type: counterOfferType,
          cashFlow: {
            payer: counterOfferType === 'offer' ? user.uid : otherUserId,
            receiver: counterOfferType === 'offer' ? otherUserId : user.uid
          },
          initiatorPedalIds: counterPedalIds,
          initiatorPedalId: counterPedalIds[0],
          isMultiPedal: counterPedalIds.length > 1
        }),
        ...(responseType === 'accept' && {
          acceptedAt: serverTimestamp(),
          paymentStatus: 'pending',
          shippingStatus: {
            shipReadyPhotos: [],
            shipReadyConfirmed: false,
            labelPurchased: false,
            trackingNumber: null,
            fromUserReady: false,
            toUserReady: false,
            fromUserPhotos: [],
            toUserPhotos: [],
            fromUser: {
              delivered: false,
              inTransit: false,
              labelPurchased: false
            },
            toUser: {
              delivered: false,
              inTransit: false,
              labelPurchased: false
            }
          },
          // Include pedal snapshots in the same update
          ...(pedalSnapshots && { pedalSnapshots })
        }),
        history: arrayUnion({
          type: 'response',
          from: user.uid,
          responseType,
          message: `${messageText}${note ? ` Note: ${note}` : ''}`,
          timestamp: new Date(),
          ...(responseType === 'counter' && (() => {
            const userIsInitiator = user.uid === tradeData.fromUserId;
            const responderPedal = userIsInitiator ? theirPedal : myPedal;
            return {
              offerSnapshot: {
                initiatorPedalIds: counterPedalIds,
                responderPedalId: tradeData.responderPedalId,
                initiatorPedalPrices: counterPedalIds.reduce((acc, id) => {
                  const pedal = initiatorListings.find(p => p.id === id);
                  if (pedal) acc[id] = Number(pedal.price || 0);
                  return acc;
                }, {}),
                responderPedalPrice: Number(responderPedal?.price || 0),
                cashAmount: Number(counterAmount || 0),
                cashType: counterOfferType,
                cashFlow: {
                  payer: counterOfferType === 'offer' ? user.uid : otherUserId,
                  receiver: counterOfferType === 'offer' ? otherUserId : user.uid
                },
                initiatorId: tradeData.fromUserId,
                responderId: tradeData.toUserId,
                initiatorPedalDetails: counterPedalIds.reduce((acc, id) => {
                  const pedal = initiatorListings.find(p => p.id === id);
                  if (pedal) {
                    acc[id] = {
                      name: `${pedal.make} ${pedal.model}`,
                      imageUrl: pedal.images?.top || null,
                      placeholderUrl: pedal.placeholderImage?.url || null,
                      condition: pedal.condition || null,
                    };
                  }
                  return acc;
                }, {}),
                responderPedalDetail: {
                  name: `${responderPedal?.make} ${responderPedal?.model}`,
                  imageUrl: responderPedal?.images?.top || null,
                  placeholderUrl: responderPedal?.placeholderImage?.url || null,
                  condition: responderPedal?.condition || null,
                }
              }
            };
          })())
        })
      });
      
      console.log('✅ Trade response submitted successfully');
      markConfirmed(tradeId, ledgerOpId);
      setSubmitPhase('submitted');

      // The response is durable as of the resolved write above. The user gets
      // their receipt NOW; the housekeeping below (inTrade flags, legacy
      // refresh) runs detached so a slow or dead connection can never again
      // hold the confirmation hostage (2026-09-25: a decline appeared lost
      // because the receipt waited behind 6+ round trips of housekeeping the
      // user never stayed to watch). Each block keeps its own try/catch, and
      // the live offer subscription refreshes page state regardless.
      const runPostResponseTail = async () => {
      // 🔒 CRITICAL: Mark both pedals as inTrade when accepting (client-side fallback)
      // This ensures pedals are marked even if Cloud Function fails or is delayed
      if (responseType === 'accept') {
        console.log('🔒 Trade accepted - marking all pedals as inTrade');
        try {
          const batch = writeBatch(db);
          let pedalUpdates = 0;
          
          const allPedalIds = getInitiatorPedalIds(tradeData);
          for (const pedalId of allPedalIds) {
            const pedalRef = doc(db, 'listings', pedalId);
            const pedalDocSnap = await getDoc(pedalRef);
            if (pedalDocSnap.exists()) {
              batch.update(pedalRef, {
                inTrade: true,
                tradeAcceptedAt: serverTimestamp(),
                tradeId: tradeId
              });
              pedalUpdates++;
            }
          }
          
          if (tradeData.responderPedalId) {
            const responderPedalRef = doc(db, 'listings', tradeData.responderPedalId);
            const responderPedalDoc = await getDoc(responderPedalRef);
            if (responderPedalDoc.exists()) {
              batch.update(responderPedalRef, {
                inTrade: true,
                tradeAcceptedAt: serverTimestamp(),
                tradeId: tradeId
              });
              pedalUpdates++;
            }
          }
          
          if (pedalUpdates > 0) {
            await batch.commit();
            console.log(`✅ Successfully marked ${pedalUpdates} pedals as inTrade for trade ${tradeId}`);
          }
        } catch (pedalError) {
          console.error('❌ Error marking pedals as inTrade:', pedalError);
        }
      }

      // 🔓 Clear inTrade flag when trade is declined to make pedals available again
      if (responseType === 'decline') {
        console.log('🔓 Trade declined - clearing inTrade flag from user\'s pedals');
        try {
          const batch = writeBatch(db);
          let pedalUpdates = 0;
          const currentUserId = user.uid;
          
          const allPedalIds = getInitiatorPedalIds(tradeData);
          for (const pedalId of allPedalIds) {
            const pedalDocSnap = await getDoc(doc(db, 'listings', pedalId));
            if (pedalDocSnap.exists() && pedalDocSnap.data().ownerId === currentUserId) {
              batch.update(doc(db, 'listings', pedalId), {
                inTrade: false,
                tradeDeclinedAt: serverTimestamp()
              });
              pedalUpdates++;
            }
          }
          
          if (tradeData.responderPedalId) {
            const responderPedalDoc = await getDoc(doc(db, 'listings', tradeData.responderPedalId));
            if (responderPedalDoc.exists() && responderPedalDoc.data().ownerId === currentUserId) {
              const responderPedalRef = doc(db, 'listings', tradeData.responderPedalId);
              batch.update(responderPedalRef, {
                inTrade: false,
                tradeDeclinedAt: serverTimestamp()
              });
              pedalUpdates++;
              console.log(`🔓 Clearing inTrade from user's responder pedal ${tradeData.responderPedalId}`);
            }
          }
          
          if (pedalUpdates > 0) {
            await batch.commit();
            console.log(`✅ Successfully cleared inTrade from ${pedalUpdates} user-owned pedals`);
          } else {
            console.log('ℹ️ No user-owned pedals to clear inTrade from');
          }
        } catch (pedalError) {
          console.error('❌ Error clearing inTrade flag:', pedalError);
          // Don't fail the trade decline if pedal update fails
        }
      }

      // Legacy refresh, retained but detached: the live subscription delivers
      // the same updated offer on its own; this only backfills derived pedal
      // state a moment sooner. Non-accept only, as before.
      if (responseType !== 'accept') {
        // Refetch the updated trade data to show the new history
        try {
          const offerRef = doc(db, 'tradeOffers', tradeId);
          const offerSnap = await getDoc(offerRef);
          if (offerSnap.exists()) {
            const updatedData = offerSnap.data();
            setTradeData({ id: offerSnap.id, ...updatedData });
            
            const defaultCounterType = updatedData.type === 'ask' ? 'offer' : 'ask';
            setCounterOfferType(defaultCounterType);

            const updatedPedalIds = getInitiatorPedalIds(updatedData);
            const pedalSnaps = await Promise.all(
              updatedPedalIds.map(id => getDoc(doc(db, 'listings', id)))
            );
            const updatedPedals = pedalSnaps
              .filter(s => s.exists())
              .map(s => ({ id: s.id, ...s.data() }));
            setInitiatorPedals(updatedPedals);
            if (updatedPedals.length > 0) {
              const isInit = currentUserId === updatedData.fromUserId;
              setMyPedal(isInit ? updatedPedals[0] : myPedal);
              setTheirPedal(isInit ? theirPedal : updatedPedals[0]);
            }
            
            console.log('✅ Trade data refreshed after response submission');
          }
        } catch (refreshError) {
          console.error('❌ Error refreshing trade data:', refreshError);
        }
      }
      }; // end runPostResponseTail

      if (responseType === 'accept') {
        // Fire the housekeeping and go: the confirm page must not wait on it.
        runPostResponseTail();
        navigate(`/trade-confirm/${tradeId}`);
      } else {
        // Receipt first, housekeeping after.
        setResponseType('');
        setCounterAmount('');
        setNote('');
        setLoading(false);
        runPostResponseTail();
        alert('Response submitted!');
      }

    } catch (err) {
      console.error('🔥 Detailed error on trade response:', err);
      if (ledgerOpId) markFailed(tradeId, ledgerOpId, err?.code || err?.name || 'unknown');
      setSubmitPhase('failed');
      setErrorMsg('Something went wrong. Please try again.');
    }

    setLoading(false);
  };

  // Show message if offer was deleted/retracted
  if (offerNotFound) {
    return (
      <div style={{ 
        minHeight: '50vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'Nunito, sans-serif'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#856404',
            marginBottom: '1rem'
          }}>
            Offer No Longer Available
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#856404',
            lineHeight: '1.6',
            marginBottom: '1.5rem'
          }}>
            This trade offer has been retracted by the other user or is no longer available.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              backgroundColor: '#C9A227',
              color: 'white',
              padding: '0.75rem 2rem',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'Nunito, sans-serif'
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (pedalUnavailable) {
    return (
      <div style={{ 
        minHeight: '50vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          backgroundColor: '#fff3cd',
          border: '2px solid #ffc107',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'Nunito, sans-serif'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#856404',
            marginBottom: '1rem'
          }}>
            Pedal No Longer Available
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#856404',
            lineHeight: '1.6',
            marginBottom: '1.5rem'
          }}>
            One or more pedals in this offer have been removed or deactivated by their owner. This offer is no longer valid.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              backgroundColor: '#C9A227',
              color: 'white',
              padding: '0.75rem 2rem',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'Nunito, sans-serif'
            }}
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  if (notLoggedIn) {
    return (
      <div style={{
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem'
      }}>
        <div style={{
          maxWidth: '500px',
          width: '100%',
          backgroundColor: '#e8f4fd',
          border: '2px solid #5A9ECB',
          borderRadius: '12px',
          padding: '2rem',
          textAlign: 'center',
          fontFamily: 'Nunito, sans-serif'
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            color: '#1a5276',
            marginBottom: '1rem'
          }}>
            Log In to View This Offer
          </h2>
          <p style={{
            fontSize: '1rem',
            color: '#2c3e50',
            lineHeight: '1.6',
            marginBottom: '1.5rem'
          }}>
            You need to be logged in to view trade offer details.
          </p>
          <button
            onClick={() => navigate('/login')}
            style={{
              backgroundColor: '#5A9ECB',
              color: 'white',
              padding: '0.75rem 2rem',
              border: 'none',
              borderRadius: '6px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: 'pointer',
              fontFamily: 'Nunito, sans-serif'
            }}
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Early exit for declined offers where pedal data is unavailable
  if (tradeData && tradeData.status === 'declined' && (!myPedal || !theirPedal)) {
    const offerSnap = tradeData.history?.find((e) => e.offerSnapshot)?.offerSnapshot || tradeData.history?.[0]?.offerSnapshot || {};
    const initiatorIds = getInitiatorPedalIds(tradeData);
    const leftPedals = initiatorIds
      .map((id) => resolvePedalDisplayFromTrade(tradeData, id))
      .filter(Boolean);
    const rightPedal = tradeData.responderPedalId
      ? resolvePedalDisplayFromTrade(tradeData, tradeData.responderPedalId)
      : null;
    const cashAmount = tradeData.cashAmount || offerSnap.cashAmount || 0;
    const cashType = tradeData.type || offerSnap.cashType || 'straight';
    const declineEntry = (tradeData.history || []).find(e => 
      // 'Trade declined' is the legacy prefix; offers declined before the
      // wording change still carry it.
      (e.type === 'response' && !e.offerSnapshot &&
        (e.message?.startsWith('Passed on this trade') || e.message?.startsWith('Trade declined'))) ||
      e.type === 'auto_decline'
    );
    const isAutoDecline = declineEntry?.type === 'auto_decline';
    const declineMessage = declineEntry?.message || null;
    const viewerIsFromUser = tradeData.fromUserId === currentUserId;
    const leftUser = viewerIsFromUser ? myUserData : theirUserData;
    const rightUser = viewerIsFromUser ? theirUserData : myUserData;

    const renderPedalColumn = (pedals, userData, isTheirColumn) => (
      <div className="offer-pedal-card" style={{
        flex: 1,
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '1rem',
        boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
        textAlign: 'left',
        ...(isTheirColumn ? { border: '2px dashed #C9A227', position: 'relative' } : {}),
      }}>
        {isTheirColumn && <TheirPedalBadge />}
        {userData?.username && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ position: 'relative', width: '32px', height: '32px', flexShrink: 0 }}>
              <img src={userData.photoURL || '/images/default-avatar.png'} alt={userData.username} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
              {getMembershipProfileBadge(userData.membershipTier)}
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937' }}>{userData.username}</div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                <span>{getMembershipTierText(userData.membershipTier)}</span>
                <span>·</span>
                <span style={{ color: '#f59e0b' }}>★</span>
                <span style={{ fontWeight: '600', color: '#374151' }}>{userData.averageRating > 0 ? userData.averageRating.toFixed(1) : '—'}</span>
                {userData.badge !== null && userData.badge !== undefined && (
                  <span>({userData.badge})</span>
                )}
              </div>
            </div>
          </div>
        )}
        {pedals.length > 0 ? pedals.map((p, i) => (
          <div key={p.id || i} style={{
            marginBottom: pedals.length > 1 && i < pedals.length - 1 ? '12px' : '0',
            paddingBottom: pedals.length > 1 && i < pedals.length - 1 ? '12px' : '0',
            borderBottom: pedals.length > 1 && i < pedals.length - 1 ? '1px solid #e5e7eb' : 'none',
          }}>
            <OfferPedalFields pedal={p} tradeStatus={tradeData.status} />
          </div>
        )) : (
          <div style={{ aspectRatio: '1', background: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Pedal unavailable</span>
          </div>
        )}
      </div>
    );

    return (
      <div className="offer-page" style={{ backgroundColor: '#F7F3EB', minHeight: '100vh', padding: '0 1rem', paddingBottom: 'calc(80px + env(safe-area-inset-bottom))' }}>
      <div className="offer-page-content" style={{ paddingTop: '2rem', paddingBottom: '2rem' }}>
        {renderOfferStickyHeader()}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 0.5rem', fontFamily: 'Nunito, sans-serif' }}>
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
          {renderPedalColumn(leftPedals, leftUser, !viewerIsFromUser)}
          {renderPedalColumn(rightPedal ? [rightPedal] : [], rightUser, viewerIsFromUser)}
        </div>

        {/* Cash amount */}
        {cashAmount > 0 && (
          <div style={{ textAlign: 'center', marginBottom: '1rem', fontSize: '0.95rem', color: '#6b7280' }}>
            {cashType === 'ask' ? `+ $${cashAmount} cash requested` : cashType === 'offer' ? `+ $${cashAmount} cash offered` : ''}
          </div>
        )}

        {/* Decline response */}
        {declineMessage && (
          <div style={{ background: '#fefce8', border: '1.5px solid #C9A227', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '1.5rem' }}>
            {isAutoDecline ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937' }}>Automatically Declined</span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0, fontWeight: '600' }}>{autoDeclineReasonText(tradeData)}</p>
              </>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  {theirUserData?.photoURL && <img src={theirUserData.photoURL} alt={theirUserData.username} style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'cover' }} />}
                  <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937' }}>{theirUserData?.username} responded</span>
                </div>
                <p style={{ fontSize: '0.875rem', color: '#374151', margin: 0, fontWeight: '600' }}>{declineMessage}</p>
              </>
            )}
          </div>
        )}

        {/* Declined status: a system close is not the other member passing on it */}
        <div className="p-3 bg-gray-50 border border-gray-200 rounded" style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
          {isAutoDeclined(tradeData) ? (
            <>
              <p className="text-gray-800"><strong>Status:</strong> Closed automatically</p>
              <p className="text-sm text-gray-600 mt-1">{autoDeclineReasonText(tradeData)}</p>
            </>
          ) : (
            <>
              <p className="text-gray-800"><strong>Status:</strong> Not a fit this time</p>
              <p className="text-sm text-gray-600 mt-1">This one didn't work out. That's a normal part of trading.</p>
            </>
          )}
        </div>

        {/* CTA */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <p style={{ fontSize: '0.95rem', color: '#6b7280', marginBottom: '1rem' }}>Don't give up. There are members who already want your pedals. See who's looking for what you have.</p>
          <a href="/recommendations" style={{ display: 'inline-block', backgroundColor: '#C9A227', color: 'white', padding: '12px 28px', borderRadius: '9999px', fontWeight: '700', fontSize: '1rem', textDecoration: 'none', fontFamily: 'Nunito, sans-serif' }}>See Who Wants Your Pedals</a>
        </div>

        <div style={{ textAlign: 'center', padding: '2rem 0', fontSize: '0.875rem', color: '#6b7280' }}>
          Need help or found a bug?{' '}
          <button
            type="button"
            onClick={() => openContact({ category: 'general' })}
            style={{ color: '#5A9ECB', background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', textDecoration: 'underline' }}
          >
            Contact PXC Support
          </button>
        </div>
      </div>
      </div>
      </div>
    );
  }

  const isTerminalOffer = tradeData && TERMINAL_TRADE_STATUSES.includes(tradeData.status);
  // Gate uses offerPageReady (computed with the hooks above) so the readiness
  // effect and this branch can never disagree about what "loaded" means.
  if (!offerPageReady) {
    // Honest connection-problem state: only reachable before the first
    // successful render, and it never claims the offer doesn't exist —
    // offerNotFound (server-confirmed only) is handled far above. If data
    // arrives while this is showing, offerPageReady flips and the full page
    // renders on the next pass.
    if (loadProblem) {
      return (
        <div className="p-4" style={{ textAlign: 'center', paddingTop: '4rem' }}>
          <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '1.05rem', color: '#4A5759', maxWidth: '420px', margin: '0 auto 1.25rem auto', lineHeight: 1.5 }}>
            We&rsquo;re having trouble loading this offer. Check your connection and try again.
          </p>
          <button
            onClick={handleLoadRetry}
            style={{
              fontFamily: 'Nunito, sans-serif',
              fontSize: '1rem',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: '#4A9FCA',
              border: 'none',
              borderRadius: '50px',
              padding: '0.6rem 2rem',
              cursor: 'pointer'
            }}
          >
            Try again
          </button>
          {/* Admin-only diagnostics (shared component; also on Profile). */}
          {isAdmin && <AdminDiagnostics />}
        </div>
      );
    }
    return <div className="p-4">Loading trade...</div>;
  }



  // Expired offers now render the full page with a banner (same as declined)
  const responseMessages = tradeData.history?.filter(entry => entry.type === 'response') || [];

  // Determine who made the last move (only offer/response entries affect turns, not notes)
  const actionEntries = tradeData.history?.filter(entry => entry.type === 'offer' || entry.type === 'response') || [];
  const lastActionEntry = actionEntries.length > 0 ? actionEntries[actionEntries.length - 1] : null;
  const lastResponderId = lastActionEntry ? lastActionEntry.from : null;
  const isOfferActive = ['pending', 'countered'].includes(tradeData.status);
  const canRespond = currentUserId && lastResponderId && currentUserId !== lastResponderId && isOfferActive;
  const mustWait = currentUserId && lastResponderId && currentUserId === lastResponderId && isOfferActive;

  const getExpirationTime = (offer) => {
    const refTimestamp = (offer.status === 'countered')
      ? (offer.offerUpdatedAt || offer.updatedAt || offer.createdAt)
      : offer.createdAt;
    if (!refTimestamp) return null;
    
    const refDate = refTimestamp.toDate ? refTimestamp.toDate() : new Date(refTimestamp);
    const expirationDate = new Date(refDate.getTime() + (48 * 60 * 60 * 1000));
    const now = new Date();
    
    const timeLeft = expirationDate.getTime() - now.getTime();
    
    if (timeLeft <= 0) {
      return 'Expired';
    }
    
    const hoursLeft = Math.floor(timeLeft / (1000 * 60 * 60));
    const minutesLeft = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hoursLeft > 24) {
      const daysLeft = Math.floor(hoursLeft / 24);
      const remainingHours = hoursLeft % 24;
      return `${daysLeft}d ${remainingHours}h remaining`;
    } else if (hoursLeft > 0) {
      return `${hoursLeft}h ${minutesLeft}m remaining`;
    } else {
      return `${minutesLeft}m remaining`;
    }
  };

  return (
    <div className="offer-page min-h-screen" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '0 1rem' }}>
      <div className="offer-page-content" style={{ paddingTop: '2rem', paddingBottom: '0', flex: 1, display: 'flex', flexDirection: 'column' }}>
      {renderOfferStickyHeader()}
      <AdminCancelledBanner offer={tradeData} kind="trade" />
      {/* Prior-response verdict banner (offerResponseLedger). 'lost' is only
          shown when the offer provably didn't change since the attempt; the
          ambiguous case never asserts a system loss. */}
      {priorResponseNotice && submitPhase === 'idle' && (
        <div style={{
          backgroundColor: priorResponseNotice.verdict === 'lost' ? '#fdecea' : '#fff8e6',
          border: `1px solid ${priorResponseNotice.verdict === 'lost' ? '#dc2626' : '#C9A227'}`,
          borderRadius: '10px',
          padding: '0.75rem 1rem',
          margin: '0 0 1rem 0',
          fontFamily: 'Nunito, sans-serif',
          color: '#4A5759',
          fontSize: '0.95rem',
          lineHeight: 1.5
        }}>
          <strong>
            {priorResponseNotice.verdict === 'lost'
              ? 'Your earlier response was not recorded.'
              : 'Your earlier response may not have been recorded because this offer changed.'}
          </strong>{' '}
          {priorResponseNotice.verdict === 'lost'
            ? 'Please review the offer below and submit it again.'
            : 'Please review the latest terms below before responding again.'}
          <button
            onClick={() => { clearRecord(tradeId); setPriorResponseNotice(null); }}
            style={{ marginLeft: '0.75rem', background: 'none', border: 'none', color: '#4A9FCA', cursor: 'pointer', fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', fontWeight: 700, padding: 0 }}
          >
            Dismiss
          </button>
        </div>
      )}
      <style>{`
        .offer-listing-link {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          color: #5A9ECB;
          font-size: 0.95rem;
          font-weight: 700;
          text-decoration: none;
          padding: 4px 0;
        }
        .offer-listing-link:hover { text-decoration: underline; }
        /* Tile photo as the tap target (see OfferPedalFields). Block with the
           image's own rounded corners so the clickable area IS the photo. */
        .offer-pedal-tile-link { display: block; line-height: 0; border-radius: 8px; overflow: hidden; cursor: pointer; }
        .offer-pedal-tile-link:hover .offer-pedal-img { filter: brightness(0.96); }
        .offer-page-header-block {
          margin-bottom: 0.75rem;
        }
        .offer-page-header-title {
          text-align: left;
        }
        .offer-page-status-stack {
          display: flex;
          flex-direction: column;
          gap: 0.35rem;
          margin-bottom: 0.75rem;
          align-items: stretch;
          width: 100%;
          box-sizing: border-box;
        }
        .offer-page-status-expires {
          padding: 0.35rem 0 !important;
          text-align: left !important;
          width: 100%;
          box-sizing: border-box;
          background: transparent !important;
          border: none !important;
        }
        .offer-page-status-expires p {
          text-align: left !important;
          margin: 0;
          font-size: calc(0.875rem + 1pt) !important;
        }
        .offer-page-status-seen,
        .offer-page-status-unseen {
          margin-bottom: 0 !important;
          width: 100%;
          text-align: left;
          font-weight: 600;
        }
        .offer-page-tile-column {
          display: flex;
          flex-direction: column;
          align-items: stretch;
          width: 100%;
          box-sizing: border-box;
        }
        .counter-offer-break {
          display: none;
        }
        @media (max-width: 768px) {
          .counter-offer-break {
            display: block;
          }
        }
        @media (min-width: 769px) {
          .offer-page-tile-column {
            width: min(calc(50% + 2rem), 100%) !important;
            min-width: min(calc(800px + 2rem), 100%) !important;
            max-width: none !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          .offer-cards-row {
            gap: 1.5rem !important;
          }
          .offer-pedal-card img.offer-pedal-img {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 1;
          }
          .offer-listing-link {
            font-size: 1.05rem !important;
            padding: 6px 0 !important;
          }
          .offer-page-main-container {
            width: 100% !important;
            min-width: 0 !important;
          }
          .offer-page-lower-sections {
            width: 100% !important;
            min-width: 0 !important;
          }
          .offer-note-textarea {
            width: 100% !important;
            box-sizing: border-box !important;
          }
          .counter-pedal-grid-scroll {
            max-height: 600px !important;
          }
          .history-entry-card {
            max-width: 85% !important;
          }
          .history-entry-mine {
            align-self: flex-end !important;
          }
          .history-entry-theirs {
            align-self: flex-start !important;
          }
        }
        @media (min-width: 769px) and (max-width: 1100px) {
          .offer-pedal-card img.offer-pedal-img {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 1;
          }
        }
        @media (max-width: 768px) {
          .offer-cards-row {
            gap: 0.75rem !important;
          }
          .offer-pedal-card {
            flex: 1 1 calc(50% - 0.375rem) !important;
            padding: 0.75rem !important;
          }
          .offer-pedal-card img.offer-pedal-img {
            width: 100% !important;
            height: auto !important;
            aspect-ratio: 1;
          }
          .offer-page-middle {
            flex: 1 1 100% !important;
            order: 3 !important;
            margin-top: 1rem !important;
          }
          .offer-page-main-container {
            width: 100% !important;
            min-width: 0 !important;
            margin: 0 0 1rem 0 !important;
          }
          .offer-page-lower-sections {
            width: 100% !important;
            min-width: 0 !important;
            margin: 0 !important;
          }
          .offer-page-status-stack {
            margin-bottom: 0.75rem;
          }
          .offer-page-tile-column {
            width: auto !important;
            min-width: 0 !important;
            margin-left: 0.5rem !important;
            margin-right: 0.5rem !important;
          }
          .offer-page-header-block {
            display: none !important;
          }
          .offer-page-status-seen,
          .offer-page-status-unseen {
            font-size: 0.9rem !important;
          }
          .comment-subtitle {
            font-size: 0.8rem !important;
          }
          .respond-options-list {
            gap: 5px !important;
          }
          .offer-respond-container {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .offer-response-buttons {
            max-width: none !important;
            width: 100% !important;
          }
          .counter-modal-pedal-grid {
            max-height: 180px !important;
          }
          .offer-note-textarea {
            min-width: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            margin-left: auto !important;
            margin-right: auto !important;
            display: block !important;
          }
          .offer-textarea-button-container {
            display: flex !important;
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .offer-page-footer {
            padding-bottom: 100px !important;
          }
          .history-entry-username {
            font-size: 0.78rem !important;
          }
          .history-entry-badge {
            font-size: 0.6rem !important;
            padding: 2px 6px !important;
          }
          .history-entry-date {
            font-size: 0.75rem !important;
          }
          .history-entry-card {
            max-width: 80% !important;
          }
          .history-entry-mine {
            align-self: flex-end !important;
          }
          .history-entry-theirs {
            align-self: flex-start !important;
          }
        }
      `}</style>

      <div className="offer-page-tile-column">
      {renderOfferPageHeaderBlock()}

      {/* Expiration + seen status */}
      <div className="offer-page-status-stack">
      {tradeData.createdAt && (
        <div className="offer-page-status-expires">
          <p className="text-orange-800 text-sm" style={{ margin: 0, textAlign: 'left' }}>
            <strong>Offer expires:</strong> {getExpirationTime(tradeData)}
          </p>
        </div>
      )}
      {(() => {
        const actionEntries = (tradeData.history || []).filter(e => e.type === 'offer' || e.type === 'response');
        const lastActionFrom = actionEntries.length > 0 ? actionEntries[actionEntries.length - 1].from : null;
        const isActiveStatus = tradeData.status === 'pending' || tradeData.status === 'countered';
        const recipientUid = (isActiveStatus && lastActionFrom)
          ? (lastActionFrom === tradeData.fromUserId ? tradeData.toUserId : tradeData.fromUserId)
          : null;
        if (!recipientUid) return null;
        const stateMs = tradeData.offerUpdatedAt?.toMillis?.() ?? 0;
        if (!(stateMs > 0)) return null;
        const viewedMs = tradeData.viewedAt?.[recipientUid]?.toMillis?.() ?? 0;
        const isSeen = viewedMs >= stateMs;
        const iAmRecipient = currentUserId === recipientUid;
        // Don't show "Not seen" to the recipient themselves — their viewedAt
        // write is fire-and-forget, so the page would otherwise display "Not
        // seen" for the entire session even though they're literally viewing.
        if (!isSeen && iAmRecipient) return null;
        if (isSeen) {
          const viewedDate = tradeData.viewedAt[recipientUid].toDate();
          const viewedStr = viewedDate.toLocaleDateString() + ' · ' + viewedDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
          return (
            <div className="offer-page-status-seen" style={{ fontSize: '1rem', color: '#16a34a' }}>
              Offer seen: {viewedStr}
            </div>
          );
        }
        return (
          <div className="offer-page-status-unseen" style={{ fontSize: '1rem', color: '#9ca3af' }}>
            Offer not yet seen
          </div>
        );
      })()}
      </div>

      {/* Pedal Cards Row — terminal offers match Trade Center (initiator left, responder right) */}
      <div className="offer-cards-row offer-page-main-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
      {isTerminalOffer ? (() => {
        const viewerIsFromUser = currentUserId === tradeData.fromUserId;
        const leftPedals = initiatorPedals;
        const rightPedals = soughtPedal ? [soughtPedal] : [];

        const renderUserHeader = (userData) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <div style={{ position: 'relative', width: '32px', height: '32px', flexShrink: 0 }}>
              <img src={userData.photoURL || '/images/default-avatar.png'} alt="avatar"
                style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
              {getMembershipProfileBadge(userData.membershipTier)}
            </div>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937' }}>{userData.username}</div>
              <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                <span>{getMembershipTierText(userData.membershipTier)}</span>
                <span>·</span>
                <span style={{ color: '#f59e0b' }}>★</span>
                <span style={{ fontWeight: '600', color: '#374151' }}>{userData.averageRating > 0 ? userData.averageRating.toFixed(1) : '—'}</span>
                {userData.badge !== null && userData.badge !== undefined && (
                  <span>({userData.badge})</span>
                )}
              </div>
            </div>
          </div>
        );

        const renderPedalDetails = (pedal, idx, total) => (
          <div key={pedal.id || idx} style={{
            marginBottom: total > 1 && idx < total - 1 ? '12px' : '0',
            paddingBottom: total > 1 && idx < total - 1 ? '12px' : '0',
            borderBottom: total > 1 && idx < total - 1 ? '1px solid #e5e7eb' : 'none',
          }}>
            <OfferPedalFields pedal={pedal} tradeStatus={tradeData.status} />
          </div>
        );

        const renderColumn = (pedals, userData, isTheirColumn) => (
          <div className="offer-pedal-card" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1rem',
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
            flex: '1',
            textAlign: 'left',
            ...(isTheirColumn ? { border: '2px dashed #C9A227', position: 'relative' } : {}),
          }}>
            {isTheirColumn && <TheirPedalBadge />}
            {renderUserHeader(userData)}
            {pedals.length > 0 ? pedals.map((pedal, idx) => renderPedalDetails(pedal, idx, pedals.length)) : (
              <div style={{ aspectRatio: '1', background: '#f3f4f6', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>Pedal unavailable</span>
              </div>
            )}
          </div>
        );

        return (
          <>
            {renderColumn(leftPedals, viewerIsFromUser ? myUserData : theirUserData, !viewerIsFromUser)}
            {renderColumn(rightPedals, viewerIsFromUser ? theirUserData : myUserData, viewerIsFromUser)}
          </>
        );
      })() : (
      <>
        {/* Your Pedal Card */}
        <div className="offer-pedal-card" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          flex: '1',
          textAlign: 'left'
        }}>
          {(() => {
            const isCurrentUserInitiator = currentUserId === tradeData.fromUserId;
            const myPedalsToShow = (isCurrentUserInitiator && initiatorPedals.length > 1 ? initiatorPedals : [myPedal]).filter(Boolean);
            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ position: 'relative', width: '32px', height: '32px', flexShrink: 0 }}>
                    <img src={myUserData.photoURL || '/images/default-avatar.png'} alt="avatar"
                      style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                    {getMembershipProfileBadge(myUserData.membershipTier)}
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937' }}>{myUserData.username}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                      <span>{getMembershipTierText(myUserData.membershipTier)}</span>
                      <span>·</span>
                      <span style={{ color: '#f59e0b' }}>★</span>
                      <span style={{ fontWeight: '600', color: '#374151' }}>{myUserData.averageRating > 0 ? myUserData.averageRating.toFixed(1) : '—'}</span>
                      {myUserData.badge !== null && myUserData.badge !== undefined && (
                        <span>({myUserData.badge})</span>
                      )}
                    </div>
                  </div>
                </div>

                {myPedalsToShow.map((pedal, idx) => (
                  <div key={pedal.id} style={{ marginBottom: myPedalsToShow.length > 1 && idx < myPedalsToShow.length - 1 ? '12px' : '0', paddingBottom: myPedalsToShow.length > 1 && idx < myPedalsToShow.length - 1 ? '12px' : '0', borderBottom: myPedalsToShow.length > 1 && idx < myPedalsToShow.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <OfferPedalFields pedal={pedal} showListingLink tradeStatus={tradeData.status} />
                  </div>
                ))}
              </>
            );
          })()}
        </div>

        {/* Their Pedal Card */}
        <div className="offer-pedal-card" style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '1rem',
          border: '2px dashed #C9A227',
          flex: '1',
          textAlign: 'left',
          position: 'relative'
        }}>
          <TheirPedalBadge />

          {(() => {
            const isCurrentUserResponder = currentUserId === tradeData.toUserId;
            const theirPedalsToShow = (isCurrentUserResponder && initiatorPedals.length > 1 ? initiatorPedals : [theirPedal]).filter(Boolean);
            return (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <div style={{ position: 'relative', width: '32px', height: '32px', flexShrink: 0 }}>
                    <img src={theirUserData.photoURL || '/images/default-avatar.png'} alt="avatar"
                      style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
                    {getMembershipProfileBadge(theirUserData.membershipTier)}
                  </div>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: '700', fontSize: '0.9rem', color: '#1f2937' }}>{theirUserData.username}</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '4px', flexWrap: 'wrap' }}>
                      <span>{getMembershipTierText(theirUserData.membershipTier)}</span>
                      <span>·</span>
                      <span style={{ color: '#f59e0b' }}>★</span>
                      <span style={{ fontWeight: '600', color: '#374151' }}>{theirUserData.averageRating > 0 ? theirUserData.averageRating.toFixed(1) : '—'}</span>
                      {theirUserData.badge !== null && theirUserData.badge !== undefined && (
                        <span>({theirUserData.badge})</span>
                      )}
                    </div>
                  </div>
                </div>

                {theirPedalsToShow.map((pedal, idx) => (
                  <div key={pedal.id} style={{ marginBottom: theirPedalsToShow.length > 1 && idx < theirPedalsToShow.length - 1 ? '12px' : '0', paddingBottom: theirPedalsToShow.length > 1 && idx < theirPedalsToShow.length - 1 ? '12px' : '0', borderBottom: theirPedalsToShow.length > 1 && idx < theirPedalsToShow.length - 1 ? '1px solid #e5e7eb' : 'none' }}>
                    <OfferPedalFields pedal={pedal} showListingLink tradeStatus={tradeData.status} />
                  </div>
                ))}
              </>
            );
          })()}
        </div>
      </>
      )}
      </div>

      {/* Response Options */}
      <div className="mb-4 offer-page-lower-sections">
        {/* Photo gate: visible to both parties whatever the turn (the approver is
            usually the one NOT responding), so it sits outside the respond block. */}
        {isOfferActive && (
          <PhotoGatePanel offer={tradeData} collection="tradeOffers" offerId={tradeId} currentUserId={currentUserId} returnTo={`/offer/${tradeId}`} onCancelOffer={mustWait ? cancelOffer : null} cancelling={cancellingOffer} />
        )}
        {tradeData.history && tradeData.history.length > 0 && (
  <div className="mb-4 border rounded p-3 bg-blue-50" style={{ width: '100%', margin: '0 auto 1rem auto' }}>
    <h4 className="font-semibold mb-2">Trade History</h4>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
    {tradeData.history?.map((entry, index, arr) => {
  // Format timestamp to include date and 12-hour time (no seconds)
  let timestamp = 'Unknown time';
  let entryDate = null;
  if (entry.timestamp?.toDate) {
    entryDate = entry.timestamp.toDate();
  } else if (entry.timestamp) {
    const d = new Date(entry.timestamp);
    if (!isNaN(d.getTime())) entryDate = d;
  }
  if (entryDate) timestamp = formatRelativeTime(entryDate);

  let timeSeparator = null;
  if (index > 0) {
    const prevEntry = arr[index - 1];
    let prevDate = null;
    if (prevEntry.timestamp?.toDate) prevDate = prevEntry.timestamp.toDate();
    else if (prevEntry.timestamp) { const d = new Date(prevEntry.timestamp); if (!isNaN(d.getTime())) prevDate = d; }

    if (prevDate && entryDate) {
      const diffMs = Math.abs(entryDate.getTime() - prevDate.getTime());
      const diffMins = Math.floor(diffMs / 60000);
      const diffHrs = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHrs / 24);
      let timeLabel;
      if (diffDays > 0) timeLabel = `${diffDays} day${diffDays > 1 ? 's' : ''} later`;
      else if (diffHrs > 0) timeLabel = `${diffHrs} hr${diffHrs > 1 ? 's' : ''} later`;
      else if (diffMins > 0) timeLabel = `${diffMins} min later`;
      else timeLabel = 'moments later';

      timeSeparator = (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '6px 0' }}>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#d1d5db' }} />
          <span style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap', fontStyle: 'italic' }}>{timeLabel}</span>
          <div style={{ flex: 1, height: '1px', backgroundColor: '#d1d5db' }} />
        </div>
      );
    }
  }
  
  const isMine = entry.from === currentUserId;
  
  // Simple fix: match entry.from (who sent this message) to the correct username
  // entry.from is the actual user ID who sent this specific message
  const username = (entry.from === currentUserId) ? myUserData.username : theirUserData.username;
  
  const label = entry.type === 'offer' ? 'Initial Offer' : entry.type === 'note' ? 'Comment' : entry.type === 'auto_decline' ? 'Auto-Declined' : entry.responseType === 'cancel' ? 'Cancelled' : 'Response';

  // A system close (auto_decline) has no member author; never show it as the
  // other trader's action (Jason, 2026-10-20).
  const senderUserData = entry.type === 'auto_decline'
    ? { username: 'PedalxChange', photoURL: '/logo192.png' }
    : (entry.from === currentUserId) ? myUserData : theirUserData;
  const senderInitials = (senderUserData.username || '??').slice(0, 2).toUpperCase();
  const actionText = entry.type === 'offer' ? 'sent an offer' : entry.type === 'note' ? 'sent a comment' : entry.type === 'auto_decline' ? 'closed this offer automatically' : entry.responseType === 'cancel' ? 'cancelled the offer' : 'responded';

  return (
    <React.Fragment key={index}>
    {timeSeparator}
    <div
      className={`history-entry-card ${isMine ? 'history-entry-mine' : 'history-entry-theirs'}`}
      style={{
        backgroundColor: isMine ? '#f0f7fc' : '#fef9ee',
        border: isMine ? '1.5px solid #5A9ECB' : '1.5px solid #d4a843',
        borderRadius: '12px',
        padding: '16px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
        width: '100%'
      }}
    >
      {/* Header: avatar + username + action | badge + date */}
      <div className="history-entry-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0, flex: '1 1 auto' }}>
          <div style={{
            width: '28px', height: '28px', borderRadius: '50%', overflow: 'hidden',
            backgroundColor: isMine ? '#dbeafe' : '#fef3c7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '0.7rem', fontWeight: '700', color: isMine ? '#1d4ed8' : '#92400e', flexShrink: 0
          }}>
            {senderUserData.photoURL ? (
              <img src={senderUserData.photoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : senderInitials}
          </div>
          {/* Wraps: comment cards are narrower than offer cards and a long
              "username sent a comment" ran under the badge and date on mobile. */}
          <span className="history-entry-username" style={{ fontSize: '0.85rem', color: '#374151', minWidth: 0, overflowWrap: 'anywhere', lineHeight: 1.25, textAlign: 'left' }}>
            <strong>{senderUserData.username}</strong> {actionText}
          </span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, marginLeft: '8px', gap: '2px' }}>
          <span className="history-entry-badge" style={{
            fontSize: '0.75rem', backgroundColor: '#f3f4f6', color: '#6b7280',
            padding: '2px 8px', borderRadius: '9999px', fontWeight: '500', whiteSpace: 'nowrap'
          }}>{label}</span>
          <span className="history-entry-date" style={{ fontSize: '0.75rem', color: '#9ca3af', whiteSpace: 'nowrap' }}>{timestamp}</span>
        </div>
      </div>

      {/* Message text */}
      {(() => {
        const messageText = entry.message || '';
        if (entry.type === 'auto_decline') {
          return (
            <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '4px', textAlign: 'left', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {autoDeclineReasonText(tradeData)}
            </p>
          );
        }
        if (entry.type === 'note' && messageText.trim()) {
          return (
            <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '10px', textAlign: 'left', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {messageText}
            </p>
          );
        }
        if (entry.type === 'response' && !entry.offerSnapshot && messageText.trim()) {
          return (
            <p style={{ fontSize: '0.9rem', color: '#374151', marginBottom: '4px', textAlign: 'left', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {messageText}
            </p>
          );
        }
        const parsedNote = parseOfferNote(messageText);
        return parsedNote ? (
          <p style={{ fontSize: '0.9rem', color: '#6b7280', fontStyle: 'italic', marginBottom: '10px', textAlign: 'left', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
            Note: {parsedNote}
          </p>
        ) : null;
      })()}

      {/* Attached images */}
      {safeCommentPhotoUrls(entry.imageUrls).length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
          {safeCommentPhotoUrls(entry.imageUrls).map((url, idx) => (
            <a key={idx} href={url} target="_blank" rel="noopener noreferrer" style={{ display: 'block', lineHeight: 0 }}>
              <img src={url} alt={`Attachment ${idx + 1}`} style={{ maxWidth: '180px', maxHeight: '180px', width: 'auto', height: 'auto', borderRadius: '8px', objectFit: 'cover', border: '1px solid #e5e7eb' }} />
            </a>
          ))}
        </div>
      )}

      {/* Offer snapshot cards */}
      {entry.offerSnapshot && (() => {
        const snap = entry.offerSnapshot;
        const initPrices = snap.initiatorPedalPrices || {};
        const initTotal = Object.values(initPrices).reduce((s, v) => s + v, 0);
        const initCount = getInitiatorPedalIds(snap).length || Object.keys(initPrices).length;
        const cashAmt = snap.cashAmount || 0;
        const cashType = snap.cashType || 'straight';

        const isViewerInitiator = currentUserId === snap.initiatorId;
        // Responder pedal never changes — use loaded data to guard against bad snapshots (Bug 1 legacy)
        const loadedResponderPrice = isViewerInitiator ? Number(theirPedal?.price || 0) : Number(myPedal?.price || 0);
        const respPrice = loadedResponderPrice || snap.responderPedalPrice || 0;
        const viewerIsSender = currentUserId === entry.from;

        const yourPedalTotal = isViewerInitiator ? initTotal : respPrice;
        const yourPedalCount = isViewerInitiator ? initCount : 1;
        const theirPedalTotal = isViewerInitiator ? respPrice : initTotal;
        const theirPedalCount = isViewerInitiator ? 1 : initCount;
        const viewerPaysCash = cashAmt > 0 && snap.cashFlow?.payer === currentUserId;

        const leftLabel = viewerIsSender ? 'OFFERING' : 'ASKING FOR';
        const rightLabel = viewerIsSender ? 'ASKING FOR' : 'OFFERING';

        const initDetails = snap.initiatorPedalDetails || {};
        // Snapshots are frozen at send time; a pedal that gained photos since
        // (the photo gate) should still show them here. Live listings win.
        const liveImageById = {};
        (initiatorPedals || []).forEach((lp) => { if (lp && lp.id) liveImageById[lp.id] = getPedalImageUrl(lp); });
        if (soughtPedal && soughtPedal.id) liveImageById[soughtPedal.id] = getPedalImageUrl(soughtPedal);
        const withLive = (d, id) => (d ? { ...d, imageUrl: (id && liveImageById[id]) || d.imageUrl || null } : d);
        const respDetail = withLive(snap.responderPedalDetail || null, snap.responderPedalId);
        const yourDetails = isViewerInitiator
          ? (snap.initiatorPedalIds || []).map(id => withLive(initDetails[id], id)).filter(Boolean)
          : (respDetail ? [respDetail] : []);
        const theirDetails = isViewerInitiator
          ? (respDetail ? [respDetail] : [])
          : (snap.initiatorPedalIds || []).map(id => withLive(initDetails[id], id)).filter(Boolean);

        const isStraight = cashAmt === 0;
        const yourTotal = yourPedalTotal + (viewerPaysCash ? cashAmt : 0);
        const theirTotal = theirPedalTotal + (!viewerPaysCash && cashAmt > 0 ? cashAmt : 0);
        const valueDiff = Math.abs(yourTotal - theirTotal);

        const tradeTypeLabel = isStraight ? 'Straight trade' : cashType === 'ask' ? 'Cash ask' : 'Cash offer';
        const tradeTypeDesc = isStraight
          ? (valueDiff === 0 ? 'Equal value on both sides' : `$${valueDiff} difference in pedal value`)
          : viewerPaysCash ? `You pay $${cashAmt}` : `They pay $${cashAmt}`;
        const tradeTypeBg = isStraight ? '#dcfce7' : '#fef3c7';
        const tradeTypeColor = isStraight ? '#166534' : '#92400e';

        const renderThumbnails = (details) => {
          if (!details || details.length === 0) return null;
          return (
            <div style={{ display: 'grid', gridTemplateColumns: details.length > 1 ? 'repeat(2, 48px)' : '48px', gap: '4px', justifyContent: 'start', marginBottom: '6px' }}>
              {details.map((d, i) => (
                <div key={i} style={{ width: '48px', height: '48px', borderRadius: '6px', overflow: 'hidden', backgroundColor: '#e5e7eb' }}>
                  {(d.imageUrl || d.placeholderUrl) ? (
                    <img src={d.imageUrl || d.placeholderUrl} alt={d.name || 'pedal'} style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: d.imageUrl ? 1 : 0.85 }} onError={(e) => { if (!e.currentTarget.src.endsWith(SKETCH_PLACEHOLDER_URL)) e.currentTarget.src = SKETCH_PLACEHOLDER_URL; }} />
                  ) : (
                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.5rem', color: '#9ca3af', textAlign: 'center', padding: '2px' }}>
                      {d.name || '?'}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        };

        const cardStyle = {
          flex: 1,
          backgroundColor: isMine ? '#ffffff' : '#fffdf7',
          border: isMine ? '1px solid #dce9f2' : '1px solid #f0e8d4',
          borderRadius: '10px', padding: '12px', textAlign: 'left',
          display: 'flex', flexDirection: 'column', justifyContent: 'center'
        };

        return (
          <>
            {/* Trade type badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <span style={{
                fontSize: '0.9rem', fontWeight: '600', backgroundColor: tradeTypeBg,
                color: tradeTypeColor, padding: '5px 14px', borderRadius: '9999px'
              }}>{tradeTypeLabel}</span>
              <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>{tradeTypeDesc}</span>
            </div>

            {/* Two-column pedal cards */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'stretch' }}>
              <div style={cardStyle}>
                <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#6b7280', letterSpacing: '0.05em', marginBottom: '8px' }}>{leftLabel}</div>
                {renderThumbnails(yourDetails)}
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '2px' }}>
                  {yourPedalCount} pedal{yourPedalCount > 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#111827' }}>${yourPedalTotal}</div>
                {cashAmt > 0 && viewerPaysCash && (
                  <div style={{ fontSize: '0.75rem', color: '#b91c1c', marginTop: '4px' }}>+ ${cashAmt} cash</div>
                )}
              </div>


              <div style={cardStyle}>
                <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#6b7280', letterSpacing: '0.05em', marginBottom: '8px' }}>{rightLabel}</div>
                {renderThumbnails(theirDetails)}
                <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '2px' }}>
                  {theirPedalCount} pedal{theirPedalCount > 1 ? 's' : ''}
                </div>
                <div style={{ fontSize: '1rem', fontWeight: '700', color: '#111827' }}>${theirPedalTotal}</div>
                {cashAmt > 0 && !viewerPaysCash && (
                  <div style={{ fontSize: '0.75rem', color: '#16a34a', marginTop: '4px' }}>+ ${cashAmt} cash</div>
                )}
              </div>
            </div>
          </>
        );
      })()}
    </div>
    </React.Fragment>
  );
})}
    </div>




  </div>
)}

        {/* Send a Comment — always available to both users when offer is active */}
        {isOfferActive && (
          <div style={{ width: '100%', marginTop: '2.5rem', marginBottom: '0.5rem' }}>
            <h4 className="font-semibold" style={{ marginTop: 0, marginBottom: '2px', textAlign: 'left' }}>Send a comment</h4>
            <p className="comment-subtitle" style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0, marginBottom: '6px', textAlign: 'left' }}>Ask questions or share details without making a formal response.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleCommentFileSelect}
              style={{ display: 'none' }}
            />
            <div className="offer-textarea-button-container" style={{ width: '100%' }}>
              {pendingPreviews.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                  {pendingPreviews.map((preview, idx) => (
                    <div key={idx} style={{ position: 'relative' }}>
                      <img src={preview} alt={`Preview ${idx + 1}`} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px', border: '2px solid #e0e0e0' }} />
                      <button
                        type="button"
                        onClick={() => removeCommentImage(idx)}
                        aria-label="Remove image"
                        style={{ position: 'absolute', top: '-6px', right: '-6px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.95)', color: '#e74c3c', fontSize: '14px', lineHeight: 1, cursor: 'pointer', padding: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }}
                      >×</button>
                    </div>
                  ))}
                </div>
              )}
              <AutoGrowTextarea
                placeholder="Type a comment or question..."
                maxLength={500}
                className="border rounded offer-note-textarea"
                style={{ marginBottom: '8px', padding: '12px', borderRadius: '8px', lineHeight: '1.5', display: 'block', width: '100%', border: '1px solid #e5e7eb', fontFamily: 'Nunito, sans-serif', color: '#374151', background: '#fff' }}
                rows={3}
                value={noteText}
                onChange={(e) => { setNoteText(e.target.value); setCommentError(''); }}
              />
              {commentError && (
                <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '0 0 8px', textAlign: 'left' }}>{commentError}</p>
              )}
              {!commentError && !commentCooldown.allowed && (
                <p style={{ color: '#6b7280', fontSize: '0.85rem', margin: '0 0 8px', textAlign: 'left' }}>{cooldownMessage(commentCooldown.remainingMs)}</p>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sendingNote || pendingImageFiles.length >= MAX_COMMENT_IMAGES}
                  aria-label="Add photo"
                  style={{
                    flexShrink: 0, width: '46px', height: '46px', borderRadius: '50%',
                    border: '2px solid #e0e0e0', background: '#fff',
                    cursor: (sendingNote || pendingImageFiles.length >= MAX_COMMENT_IMAGES) ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                    opacity: (sendingNote || pendingImageFiles.length >= MAX_COMMENT_IMAGES) ? 0.4 : 1
                  }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                </button>
                <button
                  onClick={handleSendNote}
                  disabled={sendingNote || !commentCooldown.allowed || (!noteText.trim() && !pendingImageFiles.length)}
                  style={{
                    backgroundColor: (sendingNote || !commentCooldown.allowed || (!noteText.trim() && !pendingImageFiles.length)) ? '#d1d5db' : '#C89F3C',
                    color: 'white',
                    border: 'none',
                    borderRadius: '25px',
                    padding: '11px 28px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: (sendingNote || !commentCooldown.allowed || (!noteText.trim() && !pendingImageFiles.length)) ? 'not-allowed' : 'pointer',
                    opacity: (sendingNote || !commentCooldown.allowed || (!noteText.trim() && !pendingImageFiles.length)) ? 0.6 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (!sendingNote && (noteText.trim() || pendingImageFiles.length)) e.target.style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    if (!sendingNote && (noteText.trim() || pendingImageFiles.length)) e.target.style.opacity = '1';
                  }}
                >
                  {sendingNote ? 'Sending...' : !commentCooldown.allowed ? `Wait ${commentCooldown.remainingSeconds}s` : 'Send comment'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Separator between comment and respond */}
        {isOfferActive && canRespond && (
          <div style={{ borderBottom: '1px solid #d9dce0', margin: '1.5rem 0 1.5rem 0' }} />
        )}

        {/* Determine who made the last move */}
        {canRespond && (
          <div className="offer-respond-container" style={{ width: '100%', margin: '0 auto' }}>
            {sellingDisabled && (
              <SellingDisabledBanner mode="inline" />
            )}
            <div style={{ marginBottom: '0.5rem' }}>
            <h4 className="font-semibold" style={{ marginTop: 0, marginBottom: '4px', textAlign: 'left' }}>Respond to offer</h4>
            <p style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: 0, marginBottom: '12px', textAlign: 'left' }}>Choose how you'd like to respond.</p>
            <div className="respond-options-list" style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginBottom: '12px' }}>
              {[
                { value: 'accept', label: 'Accept', desc: 'Start the trade' },
                { value: 'decline', label: 'Not for me, thanks', desc: 'Politely pass' },
                { value: 'counter', label: 'Counter offer', desc: 'Propose different pedals or cash' }
              ].map(opt => {
                const photosPending = opt.value === 'accept' && isPhotoGateClosed(tradeData);
                const isOptionBlocked = (sellingDisabled && (opt.value === 'accept' || opt.value === 'counter')) || photosPending;
                return (
                  <label
                    key={opt.value}
                    onClick={() => { if (!isOptionBlocked) setResponseType(opt.value); }}
                    title={photosPending ? 'Photos of every pedal in this trade must be added and approved first' : (isOptionBlocked ? 'Selling, trading & listing are paused, pay outstanding invoice to restore' : undefined)}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '12px 16px', borderRadius: '10px',
                      cursor: isOptionBlocked ? 'not-allowed' : 'pointer',
                      opacity: isOptionBlocked ? 0.5 : 1,
                      border: responseType === opt.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      backgroundColor: responseType === opt.value ? '#eff6ff' : '#f9fafb',
                      transition: 'all 0.15s'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="radio"
                        value={opt.value}
                        checked={responseType === opt.value}
                        onChange={(e) => { if (!isOptionBlocked) setResponseType(e.target.value); }}
                        disabled={isOptionBlocked}
                        style={{ flexShrink: 0, accentColor: '#3b82f6' }}
                      />
                      <span style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827' }}>{opt.label}</span>
                    </div>
                    <span style={{ fontSize: '0.8rem', color: '#9ca3af' }}>
                      {photosPending ? 'Photos pending' : (isOptionBlocked ? 'Selling paused' : opt.desc)}
                    </span>
                  </label>
                );
              })}
            </div>

            {/* Cost Breakdown for Accept */}
            {responseType === 'accept' && (() => {
              const cashAmt = Number(tradeData?.cashAmount || 0);
              const isInitiator = currentUserId === tradeData.fromUserId;
              const youPayCash = cashAmt > 0 && tradeData.cashFlow?.payer === currentUserId;
              const offerTypeToUse = youPayCash ? 'offer' : (cashAmt > 0 ? 'ask' : 'straight');
              
              const myPedalCount = isInitiator ? getInitiatorPedalIds(tradeData).length : 1;
              const breakdown = calculateCostBreakdown(cashAmt, offerTypeToUse, myPedalCount);
              if (!breakdown) return null;

              const yourPedalValue = isInitiator
                ? getInitiatorPedalIds(tradeData).reduce((sum, id) => {
                    const p = initiatorPedals?.find(l => l.id === id);
                    return sum + (p ? Number(p.price || 0) : 0);
                  }, 0)
                : Number(myPedal?.price || 0);
              const theirPedalValue = isInitiator
                ? Number(theirPedal?.price || 0)
                : getInitiatorPedalIds(tradeData).reduce((sum, id) => {
                    const p = initiatorPedals?.find(l => l.id === id);
                    return sum + (p ? Number(p.price || 0) : 0);
                  }, 0);
              const yourPedalCount = isInitiator ? getInitiatorPedalIds(tradeData).length : 1;
              const theirPedalCount = isInitiator ? 1 : getInitiatorPedalIds(tradeData).length;

              return (
                <div style={{ marginTop: '16px', marginBottom: '16px' }}>
                  {/* Value summary */}
                  <div style={{ padding: '12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: '#1e3a5f' }}>
                      <div style={{ flex: 1, borderRight: '1px solid #bfdbfe', paddingRight: '8px' }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>YOU'RE OFFERING</div>
                        <div>Pedal{yourPedalCount > 1 ? 's' : ''} ({yourPedalCount}): <strong>${yourPedalValue}</strong></div>
                        {cashAmt > 0 && youPayCash && (
                          <div>+ Cash: <strong>${cashAmt}</strong></div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>YOU'RE ASKING FOR</div>
                        <div>Pedal{theirPedalCount > 1 ? 's' : ''} ({theirPedalCount}): <strong>${theirPedalValue}</strong></div>
                        {cashAmt > 0 && !youPayCash && (
                          <div>+ Cash: <strong>${cashAmt}</strong></div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Cost breakdown */}
                  <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }} />
                  <h5 style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827', marginTop: 0, marginBottom: '10px' }}>Your cost breakdown</h5>
                  <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <span>Platform fee</span>
                      <span>
                        ${breakdown.platformFee.toFixed(2)}
                        {breakdown.pedalCount > 1 && (
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}> ($3 + $1/addtl pedal)</span>
                        )}
                      </span>
                    </div>
                    {breakdown.currentUserPaysCash && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                          <span>Cash payment</span>
                          <span>${breakdown.cashAmount.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                          <span>Cash processing fee ({breakdown.cashFeePercentage}%)</span>
                          <span>${breakdown.cashFee.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    {breakdown.currentUserReceivesCash && (
                      <>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#16a34a' }}>
                          <span>Cash you'll receive</span>
                          <span>+${breakdown.cashAmount.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#15803d' }}>
                          <span>Net cash after fees</span>
                          <span>+${breakdown.netCashReceived.toFixed(2)}</span>
                        </div>
                      </>
                    )}
                    <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '0.95rem' }}>
                      <span>Total you'll {breakdown.currentUserReceivesCash ? 'receive' : 'pay'}</span>
                      <span style={{ color: breakdown.currentUserReceivesCash ? '#15803d' : '#111827' }}>
                        {breakdown.currentUserReceivesCash ? '+' : ''}${breakdown.totalFees.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })()}

            {responseType === 'counter' && (
              <div className="mb-4">

                {/* Counter confirmed summary */}
                {counterConfirmed && (
                  <>
                    <div style={{ marginBottom: '10px', padding: '10px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontWeight: '600', fontSize: '0.85rem', color: '#374151' }}>Your Counter Offer ({counterPedalIds.length} pedal{counterPedalIds.length > 1 ? 's' : ''} selected)</span>
                        <button
                          onClick={openCounterModal}
                          style={{ fontSize: '0.75rem', color: '#5A9ECB', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                        >Edit</button>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px', marginBottom: '8px' }}>
                        {counterPedalIds.map(id => {
                          const pedal = initiatorListings.find(p => p.id === id);
                          if (!pedal) return null;
                          return (
                            <div key={id} style={{ textAlign: 'center' }}>
                              <img
                                src={pedal.images?.top || placeholderFor(pedal).url}
                                alt={`${pedal.make} ${pedal.model}`}
                                style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '6px' }}
                              />
                              <div style={{ fontSize: '0.6rem', fontWeight: 'bold', color: '#374151', marginTop: '2px', lineHeight: '1.1' }}>{pedal.make}</div>
                              <div style={{ fontSize: '0.55rem', color: '#6b7280', lineHeight: '1.1' }}>{pedal.model}</div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Value summary */}
                      {(() => {
                        const isInit = currentUserId === tradeData.fromUserId;
                        const selectedTotal = counterPedalIds.reduce((sum, id) => {
                          const pedal = initiatorListings.find(p => p.id === id);
                          return sum + (pedal ? Number(pedal.price || 0) : 0);
                        }, 0);
                        const fixedPedalValue = isInit ? Number(theirPedal?.price || 0) : Number(myPedal?.price || 0);
                        const cashAmt = Number(counterAmount || 0);
                        const youPay = counterOfferType === 'offer';

                        const offeringCount = isInit ? counterPedalIds.length : 1;
                        const offeringTotal = isInit ? selectedTotal : fixedPedalValue;
                        const askingCount = isInit ? 1 : counterPedalIds.length;
                        const askingTotal = isInit ? fixedPedalValue : selectedTotal;

                        return (
                          <div style={{ padding: '12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px', marginBottom: '12px' }}>
                            <div style={{ display: 'flex', gap: '8px', fontSize: '0.85rem', color: '#1e3a5f' }}>
                              <div style={{ flex: 1, borderRight: '1px solid #bfdbfe', paddingRight: '8px' }}>
                                <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>YOU'RE OFFERING</div>
                                <div>Pedal{offeringCount > 1 ? 's' : ''} ({offeringCount}): <strong>${offeringTotal}</strong></div>
                                {cashAmt > 0 && youPay && (
                                  <div>+ Cash: <strong>${cashAmt}</strong></div>
                                )}
                              </div>
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>YOU'RE ASKING FOR</div>
                                <div>Pedal{askingCount > 1 ? 's' : ''} ({askingCount}): <strong>${askingTotal}</strong></div>
                                {cashAmt > 0 && !youPay && (
                                  <div>+ Cash: <strong>${cashAmt}</strong></div>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Cost Breakdown */}
                    {counterAmount && (() => {
                      const cashAmt = Number(counterAmount || 0);
                      const isInit = currentUserId === tradeData.fromUserId;
                      const myPedalCount = isInit ? counterPedalIds.length : 1;
                      const breakdown = calculateCostBreakdown(cashAmt, counterOfferType, myPedalCount);
                      if (!breakdown) return null;
                      return (
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ borderBottom: '1px solid #e5e7eb', marginBottom: '12px' }} />
                          <h5 style={{ fontWeight: '600', fontSize: '0.95rem', color: '#111827', marginTop: 0, marginBottom: '10px' }}>Your cost breakdown</h5>
                          <div style={{ fontSize: '0.85rem', color: '#374151' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                              <span>Platform fee</span>
                              <span>
                                ${breakdown.platformFee.toFixed(2)}
                                {breakdown.pedalCount > 1 && (
                                  <span style={{ fontSize: '0.75rem', color: '#6b7280' }}> ($3 + $1/addtl pedal)</span>
                                )}
                              </span>
                            </div>
                            {breakdown.currentUserPaysCash && (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                  <span>Cash payment</span>
                                  <span>${breakdown.cashAmount.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#6b7280' }}>
                                  <span>Cash processing fee ({breakdown.cashFeePercentage}%)</span>
                                  <span>${breakdown.cashFee.toFixed(2)}</span>
                                </div>
                              </>
                            )}
                            {breakdown.currentUserReceivesCash && (
                              <>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#16a34a' }}>
                                  <span>Cash you'll receive</span>
                                  <span>+${breakdown.cashAmount.toFixed(2)}</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', color: '#15803d' }}>
                                  <span>Net cash after fees</span>
                                  <span>+${breakdown.netCashReceived.toFixed(2)}</span>
                                </div>
                              </>
                            )}
                            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '8px', marginTop: '4px', display: 'flex', justifyContent: 'space-between', fontWeight: '700', fontSize: '0.95rem' }}>
                              <span>Total you'll {breakdown.currentUserReceivesCash ? 'receive' : 'pay'}</span>
                              <span style={{ color: breakdown.currentUserReceivesCash ? '#15803d' : '#111827' }}>
                                {breakdown.currentUserReceivesCash ? '+' : ''}${breakdown.totalFees.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            )}

            {/* Counter Offer Modal */}
            {showCounterModal && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
                onClick={(e) => { if (e.target === e.currentTarget) dismissCounterModal(); }}>
                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '1.5rem', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)', textAlign: 'left' }}>
                  {/* Header */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                    <div>
                      <h3 style={{ fontWeight: '700', fontSize: '1.15rem', color: '#111827', margin: 0, textAlign: 'left' }}>Counter offer</h3>
                      <p style={{ fontSize: '0.95rem', color: '#6b7280', margin: '2px 0 0 0', textAlign: 'left', lineHeight: '1.4' }}>
                        Browsing {currentUserId === tradeData.fromUserId ? myUserData.username : theirUserData.username}'s pedalboard
                        <br />
                        <span style={{ fontSize: '0.85rem' }}>{counterPedalIds.length} selected</span>
                      </p>
                    </div>
                    <button onClick={dismissCounterModal} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#9ca3af', lineHeight: 1, padding: '0 4px' }}>&times;</button>
                  </div>

                  <div style={{ borderBottom: '1px solid #e5e7eb', margin: '12px 0 16px 0' }} />

                  {/* Instruction */}
                  <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '12px' }}>
                    Tap pedals to add or remove them from the offer.
                  </p>

                  {/* Pedal Grid */}
                  {loadingInitiatorListings ? (
                    <p style={{ color: '#9ca3af', fontSize: '0.85rem', textAlign: 'center' }}>Loading pedals...</p>
                  ) : (
                    <div className="counter-modal-pedal-grid" style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '12px', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '10px' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
                        {initiatorListings.map(pedal => {
                          const isSelected = counterPedalIds.includes(pedal.id);
                          const selectionIndex = counterPedalIds.indexOf(pedal.id);
                          const isInTrade = pedal.inTrade && !getInitiatorPedalIds(tradeData).includes(pedal.id);
                          const isMaxed = counterPedalIds.length >= MAX_INITIATOR_PEDALS && !isSelected;
                          const isDisabled = isInTrade || isMaxed;
                          return (
                            <div
                              key={pedal.id}
                              onClick={() => !isDisabled && toggleCounterPedal(pedal.id)}
                              style={{
                                position: 'relative',
                                border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                                borderRadius: '10px', padding: '6px',
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                opacity: isDisabled ? 0.4 : 1,
                                backgroundColor: isSelected ? '#eff6ff' : '#f9fafb',
                                transition: 'all 0.15s',
                                textAlign: 'center',
                                minWidth: 0,
                                overflow: 'hidden'
                              }}
                            >
                              {isSelected && (
                                <div style={{
                                  position: 'absolute', top: '4px', left: '4px', zIndex: 2,
                                  backgroundColor: '#3b82f6', color: 'white', fontWeight: 'bold',
                                  borderRadius: '50%', width: '20px', height: '20px', fontSize: '0.7rem',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  border: '2px solid white', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                }}>
                                  {selectionIndex + 1}
                                </div>
                              )}
                              <img
                                src={pedal.images?.top || placeholderFor(pedal).url}
                                alt={`${pedal.make} ${pedal.model}`}
                                style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: '8px', opacity: pedal.images?.top ? 1 : 0.6 }}
                              />
                              <div style={{ fontSize: '0.7rem', fontWeight: 'bold', color: '#374151', marginTop: '4px', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pedal.make}</div>
                              <div style={{ fontSize: '0.65rem', color: '#6b7280', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{pedal.model}</div>
                              <div style={{ fontSize: '0.7rem', fontWeight: '700', color: isSelected ? '#3b82f6' : '#6b7280', marginTop: '2px' }}>${Number(pedal.price || 0)}</div>
                              {isInTrade && (
                                <div style={{ fontSize: '0.6rem', color: '#ef4444', fontWeight: '600', marginTop: '2px' }}>In Trade</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* View pedalboard link */}
                  <div style={{ textAlign: 'center', marginBottom: '16px' }}>
                    <a
                      href={`/pedalboard/${tradeData.fromUserId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: '0.8rem', color: '#3b82f6', textDecoration: 'none' }}
                      onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                      onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                    >
                      <strong>View {currentUserId === tradeData.fromUserId ? (myUserData?.username || 'your') : (theirUserData?.username || 'their')}'s pedalboard</strong>
                    </a>
                  </div>

                  {/* Cash Adjustment */}
                  <div style={{ marginBottom: '12px' }}>
                    <h4 style={{ fontWeight: '600', fontSize: '0.9rem', color: '#111827', margin: '0 0 8px 0' }}>Cash adjustment</h4>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {[
                        { value: 'ask', label: 'Ask for additional $' },
                        { value: 'offer', label: <span>Offer<br className="counter-offer-break" /> additional $</span> }
                      ].map(opt => (
                        <label
                          key={opt.value}
                          className="counter-cash-option"
                          onClick={() => setCounterOfferType(opt.value)}
                          style={{
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                            padding: '10px', borderRadius: '9999px', cursor: 'pointer',
                            border: counterOfferType === opt.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                            backgroundColor: counterOfferType === opt.value ? '#eff6ff' : '#f9fafb',
                            fontSize: '0.85rem', fontWeight: counterOfferType === opt.value ? '600' : '400',
                            color: counterOfferType === opt.value ? '#1d4ed8' : '#374151',
                            transition: 'all 0.15s'
                          }}
                        >
                          <input
                            type="radio"
                            value={opt.value}
                            checked={counterOfferType === opt.value}
                            onChange={(e) => setCounterOfferType(e.target.value)}
                            style={{ accentColor: '#3b82f6', flexShrink: 0 }}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Amount */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <label style={{ fontWeight: '500', fontSize: '0.85rem', color: '#374151', whiteSpace: 'nowrap' }}>Amount ($)</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      className="border border-gray-300 focus:border-blue-500 focus:outline-none"
                      style={{ flex: 1, padding: '8px 12px', fontSize: '0.95rem', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                      value={counterAmount}
                      onChange={(e) => setCounterAmount(formatCurrencyInput(e.target.value))}
                    />
                  </div>

                  {/* Value Summary */}
                  {counterPedalIds.length > 0 && (() => {
                    const isInit = currentUserId === tradeData.fromUserId;
                    const selectedTotal = counterPedalIds.reduce((sum, id) => {
                      const pedal = initiatorListings.find(p => p.id === id);
                      return sum + (pedal ? Number(pedal.price || 0) : 0);
                    }, 0);
                    const fixedPedalValue = isInit ? Number(theirPedal?.price || 0) : Number(myPedal?.price || 0);
                    const cashAmt = Number(counterAmount || 0);
                    const youPay = counterOfferType === 'offer';

                    const offeringCount = isInit ? counterPedalIds.length : 1;
                    const offeringTotal = isInit ? selectedTotal : fixedPedalValue;
                    const askingCount = isInit ? 1 : counterPedalIds.length;
                    const askingTotal = isInit ? fixedPedalValue : selectedTotal;

                    const yourTotal = offeringTotal + (youPay ? cashAmt : 0);
                    const theirTotal = askingTotal + (!youPay ? cashAmt : 0);
                    const diff = yourTotal - theirTotal;
                    return (
                      <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '10px' }}>
                        <div style={{ display: 'flex', gap: '8px', fontSize: '0.78rem', color: '#1e3a5f' }}>
                          <div style={{ flex: 1, borderRight: '1px solid #bfdbfe', paddingRight: '8px' }}>
                            <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>YOU'RE OFFERING</div>
                            <div>Pedal{offeringCount > 1 ? 's' : ''} ({offeringCount}): <strong>${offeringTotal}</strong></div>
                            {cashAmt > 0 && youPay && (
                              <div>+ Cash: <strong>${cashAmt}</strong></div>
                            )}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '0.65rem', fontWeight: '600', color: '#64748b', letterSpacing: '0.05em', marginBottom: '4px' }}>YOU'RE ASKING FOR</div>
                            <div>Pedal{askingCount > 1 ? 's' : ''} ({askingCount}): <strong>${askingTotal}</strong></div>
                            {cashAmt > 0 && !youPay && (
                              <div>+ Cash: <strong>${cashAmt}</strong></div>
                            )}
                          </div>
                        </div>
                        <div style={{ borderBottom: '1px solid #bfdbfe', margin: '8px 0' }} />
                        <div style={{ textAlign: 'center', fontSize: '0.78rem', color: diff > 0 ? '#C9A227' : diff < 0 ? '#16a34a' : '#6b7280', fontWeight: '500' }}>
                          {diff === 0 ? 'Balanced offer' : `You'd ${diff > 0 ? 'give' : 'receive'} $${Math.abs(diff)} more in value`}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Confirm Button */}
                  <button
                    onClick={confirmCounterModal}
                    disabled={counterPedalIds.length === 0}
                    style={{
                      width: '100%', backgroundColor: counterPedalIds.length === 0 ? '#9CA3AF' : '#C9A227',
                      color: 'white', border: 'none', borderRadius: '9999px', padding: '12px',
                      fontFamily: 'Nunito, sans-serif', fontWeight: 'bold', fontSize: '15px',
                      cursor: counterPedalIds.length === 0 ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Confirm counter offer
                  </button>
                </div>
              </div>
            )}

            {responseType === 'decline' && (
              <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                <p style={{ fontSize: '0.85rem', color: '#6b7280', margin: '0 0 8px' }}>
                  Let them know why (optional).
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {DECLINE_REASONS.map(reason => {
                    const isSelected = declineReason === reason.label;
                    return (
                      <button
                        key={reason.value}
                        type="button"
                        onClick={() => {
                          if (reason.routesToCounter) {
                            setDeclineReason('');
                            setResponseType('counter');
                            return;
                          }
                          setDeclineReason(isSelected ? '' : reason.label);
                        }}
                        style={{
                          padding: '8px 14px',
                          borderRadius: '9999px',
                          border: isSelected ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                          backgroundColor: isSelected ? '#eff6ff' : '#f9fafb',
                          color: '#374151',
                          fontFamily: 'Nunito, sans-serif',
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          cursor: 'pointer',
                          transition: 'all 0.15s'
                        }}
                      >
                        {reason.label}
                      </button>
                    );
                  })}
                </div>
                <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '10px 0 0' }}>
                  A "not this time" is normal. It helps everyone find a better fit.
                </p>
              </div>
            )}

            <div className="offer-textarea-button-container" style={{ width: '100%' }}>
              <div style={{ backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '12px', marginBottom: '16px' }}>
                <AutoGrowTextarea
                  placeholder="Add a short note..."
                  maxLength={500}
                  className="offer-note-textarea"
                  style={{ width: '100%', padding: '0', lineHeight: '1.5', display: 'block', border: 'none', background: 'transparent', outline: 'none', fontFamily: 'Nunito, sans-serif', color: '#374151' }}
                  rows={3}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>

              {responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress) && (
                <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: '0.75rem', lineHeight: '1.4', textAlign: 'center' }}>
                  A shipping address is required before accepting this offer.{' '}
                  <button
                    type="button"
                    onClick={() => setShowAddressModal(true)}
                    style={{ color: '#dc2626', textDecoration: 'underline', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'inherit', fontFamily: 'inherit' }}
                  >
                    Add address
                  </button>
                </p>
              )}

              <div className="offer-response-buttons" style={{ display: 'flex', gap: '10px', maxWidth: '400px', marginLeft: 'auto' }}>
                <button
                  onClick={() => { setResponseType(''); setNote(''); setDeclineReason(''); setCounterConfirmed(false); }}
                  style={{
                    backgroundColor: '#E5E7EB',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '12px 20px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '600',
                    fontSize: '15px',
                    cursor: 'pointer',
                    flex: '1 1 0'
                  }}
                  className="hover:opacity-80 transition-opacity"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResponse}
                  style={{
                    backgroundColor: (loading || !responseType || (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress))) ? '#9CA3AF' : '#C9A227',
                    color: 'white',
                    border: 'none',
                    borderRadius: '9999px',
                    padding: '12px 20px',
                    fontFamily: 'Nunito, sans-serif',
                    fontWeight: '700',
                    fontSize: '15px',
                    cursor: (loading || !responseType || (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress))) ? 'not-allowed' : 'pointer',
                    opacity: (loading || !responseType || (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress))) ? 0.6 : 1,
                    flex: '2 1 0'
                  }}
                  disabled={loading || !responseType || (responseType === 'accept' && !isAddressComplete(firestoreUser?.shippingAddress))}
                  className="hover:opacity-90 transition-opacity"
                >
                  {loading ? 'Submitting...' : 'Submit response'}
                </button>
              </div>
            </div>

            {submitPhase === 'syncing' && (
              <p style={{ fontFamily: 'Nunito, sans-serif', fontSize: '0.9rem', color: '#4A5759', marginTop: '0.5rem' }}>
                Response queued, syncing...
              </p>
            )}
            {errorMsg && <p className="text-red-600 mt-2">{errorMsg}</p>}
            </div>
          </div>
        )}

        {mustWait && (
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto' }}>
            <p className="text-blue-800">
              <strong>Status:</strong> {tradeData.status === 'pending' ? 'Waiting for response' : 'Counter offer received'}
            </p>
            <p className="text-sm text-blue-600 mt-1">
              {tradeData.status === 'pending' 
                ? 'The recipient will respond to your offer.'
                : 'The other party will respond to your counter offer.'
              }
            </p>
            {/* Back out while waiting (Jason, 2026-10-19): same flow as the Trade
                Center's Cancel offer, so the other member is never stuck holding
                an offer the sender has abandoned. */}
            <button
              type="button"
              disabled={cancellingOffer}
              onClick={cancelOffer}
              style={{ marginTop: '10px', background: 'none', border: '1px solid #9CA3AF', borderRadius: '25px', padding: '6px 14px', fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '0.85rem', color: '#4B5563', cursor: cancellingOffer ? 'wait' : 'pointer' }}
            >
              {cancellingOffer ? 'Cancelling…' : 'Cancel offer'}
            </button>
          </div>
        )}

        {/* Show accepted status */}
        {tradeData.status === 'cancelled' && (
          <div className="mt-4 p-3 rounded" style={{ maxWidth: '600px', margin: '1rem auto', background: '#F3F4F6', border: '1px solid #D1D5DB' }}>
            <p style={{ color: '#374151' }}>
              <strong>Status:</strong> Offer cancelled
            </p>
            <p className="text-sm mt-1" style={{ color: '#6B7280' }}>
              {tradeData.cancelledBy === currentUserId ? 'You cancelled this offer.' : 'The other member cancelled this offer.'} It can no longer be accepted.
            </p>
          </div>
        )}
        {tradeData.status === 'accepted' && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto' }}>
            <p className="text-green-800">
              <strong>Status:</strong> Trade Accepted!
            </p>
            <p className="text-sm text-green-600 mt-1">
              This trade has been accepted. You can view the trade progress in your Trade Center.
            </p>
          </div>
        )}

        {/* Show expired status */}
        {tradeData.status === 'expired' && (
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto' }}>
            <p className="text-yellow-800">
              <strong>Status:</strong> Offer Expired
            </p>
            <p className="text-sm text-yellow-700 mt-1">
              This trade offer was not responded to within 48 hours and has expired.
            </p>
          </div>
        )}

        {/* Show declined status */}
        {tradeData.status === 'declined' && isAutoDeclined(tradeData) && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto', textAlign: 'center' }}>
            <p className="text-gray-800">
              <strong>Status:</strong> Closed automatically
            </p>
            <p className="text-sm text-gray-600 mt-1">
              {autoDeclineReasonText(tradeData)}
            </p>
          </div>
        )}
        {tradeData.status === 'declined' && !isAutoDeclined(tradeData) && (
          <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded" style={{ maxWidth: '600px', margin: '1rem auto 0 auto', textAlign: 'center' }}>
            <p className="text-gray-800">
              <strong>Status:</strong> Not a fit this time
            </p>
            <p className="text-sm text-gray-600 mt-1">
              This one didn't work out. That's a normal part of trading.
            </p>
            {/* Only the sender needs somewhere to go next; the decliner is already done. */}
            {tradeData.fromUserId === currentUserId && (
              <a
                href="/recommendations"
                style={{
                  display: 'inline-block', marginTop: '12px', backgroundColor: '#C9A227',
                  color: 'white', padding: '10px 22px', borderRadius: '9999px',
                  fontWeight: '700', fontSize: '0.9rem', textDecoration: 'none',
                  fontFamily: 'Nunito, sans-serif'
                }}
              >
                See who wants your pedals
              </a>
            )}
          </div>
        )}

      </div>
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>
      
      {/* Footer */}
      <div className="bg-gray-100 border-t border-gray-200 py-4 offer-page-footer" style={{ paddingBottom: '10px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button 
                onClick={() => openContact({ category: 'general' })}
                className="hover:underline"
                style={{ 
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
        </div>
      </div>
      </div>
      </div>

      {/* Inline shipping-address modal: keeps users on the page so they can
          add their address and immediately re-click Accept. */}
      <AddressRequiredModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSaved={() => setShowAddressModal(false)}
        user={firestoreUser}
        context="trade-accept"
      />

      {/* "Send Offer to Others" modal — auto-opens once per tradeId per session
          for the offer initiator if there are eligible want-list matches not
          already excluded (recipient, original pedal, or pending offers). */}
      {showBulkSendModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="bulk-send-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1rem',
            // Pad past BOTH iOS safe-area insets: the status bar at top (so the
            // modal header can't slide up under it and clip — the bug this fixes)
            // and the bottom tab bar at the bottom. env() is 0 on web, so the web
            // app is unchanged. Centering between these two keeps it on-screen.
            paddingTop: 'calc(1rem + env(safe-area-inset-top))',
            paddingBottom: 'calc(1rem + 100px + env(safe-area-inset-bottom))'
          }}
          onClick={closeBulkSendModal}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              maxWidth: '780px',
              width: '100%',
              // Mirror the overlay padding (top inset + bottom inset + tab bar) so
              // a max-height modal fits exactly in the visible area without
              // clipping the header. env() is 0 on web → unchanged there.
              maxHeight: 'calc(100vh - 2rem - 100px - env(safe-area-inset-top) - env(safe-area-inset-bottom))',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.25)'
            }}
          >
            <div style={{ padding: '1rem 1.25rem 0.75rem 1.25rem', borderBottom: '1px solid #c2c8d0' }}>
              <h2
                id="bulk-send-modal-title"
                style={{ margin: 0, fontSize: '1.35rem', fontWeight: 700, color: '#5A9ECB' }}
              >
                Want to send this same offer to others?
              </h2>
              <p style={{ margin: '0.4rem 0 0 0', fontSize: '0.95rem', color: '#4b5563', lineHeight: 1.45 }}>
                Send this exact offer to up to {BULK_SEND_MAX} other pedals on your Wantlist of similar value
              </p>
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0.75rem 1.25rem', backgroundColor: '#f3f4f6' }}>
              {bulkSendError && (
                <div
                  role="alert"
                  style={{
                    backgroundColor: '#fee2e2',
                    color: '#991b1b',
                    padding: '0.75rem 1rem',
                    borderRadius: '6px',
                    marginBottom: '1rem',
                    fontSize: '0.9rem'
                  }}
                >
                  {bulkSendError}
                </div>
              )}

              <div
                className="bulk-send-grid"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                  gap: '0.75rem'
                }}
              >
                <style jsx>{`
                  @media (max-width: 600px) {
                    .bulk-send-grid {
                      grid-template-columns: repeat(2, 1fr) !important;
                      gap: 0.5rem !important;
                    }
                  }
                `}</style>
                {bulkSendCandidates.map((pedal) => {
                  const selected = bulkSendSelectedIds.includes(pedal.id);
                  const atCap =
                    !selected && bulkSendSelectedIds.length >= BULK_SEND_MAX;
                  const thumbnail = getPrimaryPedalImage(pedal);
                  return (
                    <button
                      type="button"
                      key={pedal.id}
                      onClick={() => toggleBulkSendPedal(pedal.id)}
                      disabled={atCap || bulkSendSubmitting}
                      aria-pressed={selected}
                      style={{
                        textAlign: 'left',
                        padding: 0,
                        border: selected
                          ? '2px solid #5A9ECB'
                          : '1px solid #e5e7eb',
                        borderRadius: '10px',
                        backgroundColor: '#ffffff',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.09)',
                        cursor: atCap || bulkSendSubmitting ? 'not-allowed' : 'pointer',
                        opacity: atCap ? 0.5 : 1,
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          backgroundColor: selected ? '#5A9ECB' : 'rgba(255, 255, 255, 0.9)',
                          border: selected ? '2px solid #5A9ECB' : '2px solid #d1d5db',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#ffffff',
                          fontSize: '14px',
                          fontWeight: 700,
                          zIndex: 1
                        }}
                      >
                        {selected ? '✓' : ''}
                      </div>
                      <img
                        src={thumbnail}
                        alt={`${pedal.make || ''} ${pedal.model || ''}`.trim() || 'Pedal'}
                        style={{
                          width: '100%',
                          aspectRatio: '4 / 3',
                          objectFit: 'cover',
                          display: 'block',
                          backgroundColor: '#f3f4f6'
                        }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = BULK_SEND_FALLBACK_IMAGE;
                        }}
                      />
                      <div style={{ padding: '0.6rem 0.75rem' }}>
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: '0.95rem',
                            color: '#1f2937',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {pedal.make || 'Unknown'}
                        </div>
                        <div
                          style={{
                            fontSize: '0.85rem',
                            color: '#4b5563',
                            marginTop: '2px',
                            lineHeight: 1.2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {pedal.model || ''}
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginTop: '0.5rem',
                            fontSize: '0.85rem',
                            color: '#6b7280'
                          }}
                        >
                          <span style={{ fontWeight: 600, color: '#1f2937' }}>
                            ${Number(pedal.price || 0).toLocaleString()}
                          </span>
                          {pedal.condition && (
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 10px',
                                borderRadius: '9999px',
                                backgroundColor: '#F3F4F6',
                                color: '#374151',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                fontSize: '0.75rem'
                              }}
                            >
                              {pedal.condition}
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div
              style={{
                padding: '0.5rem 1.25rem 0rem 1.25rem',
                borderTop: '1px solid #c2c8d0',
                backgroundColor: '#ffffff'
              }}
            >
              <label
                htmlFor="bulk-send-note"
                style={{
                  display: 'block',
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: '#374151',
                  marginBottom: '0.35rem'
                }}
              >
                Message (optional)
              </label>
              <AutoGrowTextarea
                id="bulk-send-note"
                className="bulk-send-textarea"
                value={bulkSendNote}
                onChange={(e) => setBulkSendNote(e.target.value.slice(0, 500))}
                disabled={bulkSendSubmitting}
                rows={2}
                placeholder="Add a note to send with each offer..."
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontFamily: 'inherit',
                  backgroundColor: '#ffffff'
                }}
              />
              <style jsx>{`
                @media (max-width: 600px) {
                  .bulk-send-textarea {
                    min-height: 52px !important;
                  }
                }
              `}</style>
              <div
                style={{
                  fontSize: '0.75rem',
                  color: '#9ca3af',
                  textAlign: 'right',
                  marginTop: '2px'
                }}
              >
                {bulkSendNote.length}/500
              </div>
            </div>

            <div
              style={{
                padding: '0.6rem 1.25rem',
                borderTop: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '0.75rem',
                flexWrap: 'wrap'
              }}
            >
              <div style={{ fontSize: '0.9rem', color: '#4b5563' }}>
                {bulkSendSelectedIds.length} of {BULK_SEND_MAX} selected
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={closeBulkSendModal}
                  disabled={bulkSendSubmitting}
                  style={{
                    padding: '0.6rem 1.25rem',
                    borderRadius: '25px',
                    border: '1px solid #d1d5db',
                    backgroundColor: '#ffffff',
                    color: '#4b5563',
                    fontWeight: 600,
                    cursor: bulkSendSubmitting ? 'not-allowed' : 'pointer'
                  }}
                >
                  Not now
                </button>
                <button
                  type="button"
                  onClick={submitBulkSend}
                  disabled={
                    bulkSendSelectedIds.length === 0 || bulkSendSubmitting
                  }
                  style={{
                    padding: '0.6rem 1.5rem',
                    borderRadius: '25px',
                    border: 'none',
                    backgroundColor:
                      bulkSendSelectedIds.length === 0 || bulkSendSubmitting
                        ? '#9ca3af'
                        : '#5A9ECB',
                    color: '#ffffff',
                    fontWeight: 700,
                    cursor:
                      bulkSendSelectedIds.length === 0 || bulkSendSubmitting
                        ? 'not-allowed'
                        : 'pointer'
                  }}
                >
                  {bulkSendSubmitting
                    ? 'Sending...'
                    : bulkSendSelectedIds.length === 0
                    ? 'Send Offer'
                    : `Send ${bulkSendSelectedIds.length} Offer${bulkSendSelectedIds.length === 1 ? '' : 's'}`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk send toast — appears after successful submission */}
      {bulkSendToast && (
        <div
          role="status"
          style={{
            position: 'fixed',
            bottom: '1.5rem',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#1f2937',
            color: '#ffffff',
            padding: '0.85rem 1.5rem',
            borderRadius: '25px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            zIndex: 1100,
            fontSize: '0.95rem',
            fontWeight: 600
          }}
        >
          {bulkSendToast}
        </div>
      )}
    </div>
  );
};

export default OfferPage;